/**
 * Pure, side-effect-free helper functions for the documents redux module
 * (state behind the PDF document-redaction editor).
 *
 * Covers redaction-origin predicates, the overlay-weight count the render
 * window budgets against (`countOverlayRects` — one number spanning every
 * per-page overlay, shared so the editor canvas and the secondary viewers
 * cannot disagree), the PII bulk-action predicates that say
 * what Redact / Unredact / Delete may touch (`isBulkRedactable` and friends —
 * they sit next to `deriveDetectionRedaction` / `setDetectionRedacted` because
 * they describe exactly what the bulk action does to a `'partial'` detection),
 * the glyph-anchored pad/inset rules,
 * box geometry (clamping, resize, polygon-to-strips), serialization to and
 * from the persisted document-redaction asset body (including the 0.0.3
 * legacy-pad load adapter, the pre-1.1.0 page-flag name fallback, the
 * pre-VE-29686 unsearched-page list migrated from a flat array to ranges on
 * read — see `appliedLexiconsFromAssetBody` — and the
 * custom-lexicon detections whose geometry is stored rather than recomputed —
 * see `lexiconDetectionsFromAssetBody`; validating readers for the top-level
 * watermark and Bates-stamp keys), the seam-free view a RegExp has to match
 * against (`seamlessPageText`) and the rule that PII engine results must not
 * destroy rows the engine did not produce (`mergePiiResultsIntoDetections`),
 * the
 * undo/redo snapshot stack, per-page searchable text reconstruction
 * (`buildPageText` — geometry-chosen space-or-seam separator so per-glyph OCR /
 * split-word pages stay searchable) and keyword-match search over it
 * (whitespace-run tolerant, seam-aware), occurrence grouping of per-row
 * redaction boxes and the metrics derived from it, and the burn-in RedactionPlan
 * and page-bound check (`highestPageOf`) consumed by
 * src/redact-app/state/modules/documents/sagas/exportPush.ts. Types come
 * from ./models and constants from ./store; nothing here may import redux,
 * sagas, or services — these functions are property-based-test targets.
 */
import { TDOId } from '@common-modules/universal/models/Brands';
import { clampBatesSettings } from './batesLabel';

import {
  AppliedLexiconRecord,
  BoundingBox,
  Detection,
  DetectionBase,
  DetectionRectKey,
  DocumentRedactionAssetV1,
  unsearchedCoverageOf,
  PageDimensions,
  LexiconSourcedDetection,
  PersistedLexiconDetection,
  PersistedLexiconMatch,
  PersistedPoint,
  PersistedPiiMatch,
  PersistedRedactionEntry,
  AppliedWatermark,
  createImageWatermark,
  createTextWatermark,
  BatesStampSettings,
  PersistedRedactionState,
  PII_TYPE_LABELS,
  PiiDetection,
  PiiEngineResult,
  PiiMetrics,
  PiiOccurrence,
  PiiTypeId,
  KeywordMetrics,
  isLexiconDetection,
  isPiiDetection,
  RedactionCodeAssignment,
  Redaction,
  RedactionMetricsBreakdown,
  RedactionOrigin,
  SessionState,
  TextSelectMetrics,
  UndoSnapshot,
  UndoState,
  MAX_WATERMARK_TEXT_LENGTH,
} from './models';
import { sanitizeStoredImageDataUrl } from './watermarkImage';
import {
  clampWatermarkInset,
  clampWatermarkPct,
  clampWatermarkRotation,
  clampWatermarkText,
  fontScaleOf,
  WatermarkAnchorH,
  WatermarkAnchorV,
  WatermarkTextAlign,
} from './watermarkTextMetrics';

/** Narrow an asset-written anchor to the values the arithmetic recognises. */
const isAnchorH = (v: unknown): v is WatermarkAnchorH =>
  v === 'left' || v === 'center' || v === 'right';
const isAnchorV = (v: unknown): v is WatermarkAnchorV =>
  v === 'top' || v === 'middle' || v === 'bottom';
const isTextAlign = (v: unknown): v is WatermarkTextAlign =>
  v === 'left' || v === 'center' || v === 'right';
import {
  DIRECT_PII_TYPES,
  DOCUMENT_REDACTION_ASSET_FORMAT_VERSION,
  DOCUMENT_REDACTION_ASSET_FORMAT_VERSION_PRE_LEXICON,
  DOCUMENT_REDACTION_COORD_PRECISION,
  MIN_DIMENSION,
  UNDO_STACK_LIMIT,
} from './store';

/**
 * Pure helpers. PBT-targeted (PBT-01..PBT-10). All functions in this module
 * are side-effect-free and do not import from redux, sagas, or services.
 */

// ---------------------------------------------------------------------------
// Origin predicates
//
// `origin` is the single source of truth for provenance (no `selectionMode`).
// These predicates name the groupings the rest of the editor cares about so
// call sites don't re-derive them inline.
// ---------------------------------------------------------------------------

/** Keyword-search redaction (single Redact or Redact-all). */
export function isKeywordRedaction(r: { origin: RedactionOrigin }): boolean {
  return r.origin === 'keyword';
}

/** Highlight-text redaction (Redact Selection). */
export function isTextSelectRedaction(r: { origin: RedactionOrigin }): boolean {
  return r.origin === 'text-select';
}

/**
 * Anchored to the glyphs beneath the box — keyword + text-select. These share
 * the "no drag, no resize, no stick-to-all-pages" interaction contract because
 * moving/duplicating them would drift the box off its source word(s).
 */
export function isGlyphAnchoredRedaction(r: {
  origin: RedactionOrigin;
}): boolean {
  return r.origin === 'keyword' || r.origin === 'text-select';
}

// Glyph-anchored (keyword / text-select) redactions store the NEUTRAL box — the
// exact glyph bounds, no pad — as canonical geometry. Two derived boxes are
// computed independently at use time, never persisted:
//   1. Removal box (burn-in only): the stored box inset vertically by
//      GLYPH_REDACTION_REMOVAL_VINSET so MuPDF's `applyRedactions` — which
//      deletes any glyph whose box intersects the rect — can't reach the row
//      above/below in tightly-spaced docs. Horizontal stays exact (insetting
//      could drop a thin first/last glyph from removal).
//   2. Visual box (editor overlay + the burn-in black fill): the stored box
//      grown by GLYPH_REDACTION_VISUAL_[VH]PAD so it doesn't read cramped.
//
// VERTICAL visual pad is 0: the neutral box is now MuPDF's per-glyph quad, which
// is the full font LINE box (ascent-to-descent) — it already spans the text's
// full height with built-in margin, so growing it vertically only overran the
// line pitch and made a solid redaction bar read much taller than the text (a
// ~10pt line got a ~14pt bar). Horizontal is 0 as of VE-26506 — see below.
/** Vertical inset (pt) applied to the burn-in removal box. Removal only. */
export const GLYPH_REDACTION_REMOVAL_VINSET = 2;
/** MAX per-side visual pad (pt) for the editor + burn-in fill box. */
export const GLYPH_REDACTION_VISUAL_VPAD = 0;
// HORIZONTAL is 0 as of VE-26506, confirmed on a customer document: a fixed 2pt
// pad is capped only against neighbouring REDACTIONS, never against neighbouring
// GLYPHS, so wherever text is tight around a separator it painted the adjacent
// character — redacting `John` in `Smith,John` covered the comma, and redacting
// `Smith` bled into the `J` (the original report). Note the pad only ever grew
// the VISIBLE fill, never the removal rect, so that bleed hid the neighbour
// without deleting it — the confusing failure, not a leak.
//
// A pad of 0 is only SAFE because the burn-in now always paints the fill for a
// glyph-anchored rect. It used to skip it when every pad was zero, which was
// harmless while this constant kept `padLeft` non-zero and became an
// under-redaction the moment it didn't: the removal rect is inset vertically by
// GLYPH_REDACTION_REMOVAL_VINSET, so without the fill the burned bar is 2*inset
// shorter than the glyphs, and on a scanned page that leaves the ascender tops
// and descender bottoms of the redacted word visible. See burnInWorker's
// Pass 2.5. Keep these two facts together: whoever changes the pad owns that
// guarantee.
export const GLYPH_REDACTION_VISUAL_HPAD = 0;

/**
 * Load adapter for legacy (0.0.3) asset bodies. Glyph-anchored boxes were
 * stored back then with a baked-in +2 pad (the creation paths padded by 2pt);
 * the current model stores the NEUTRAL box and derives the pad at use time. So
 * on open we inset 0.0.3 glyph-anchored boxes by 2pt per side to recover the
 * neutral box — otherwise the +2 would stack with the new visual +2 (boxes read
 * ~2pt too big) and the removal box would inset from an already-padded box.
 *
 * NOTE: 0.0.3 manual-drag text-select boxes used a -2 VERTICAL convention (the
 * other paths used +2), so those land ~2pt tight vertically after this inset.
 * The burn-in inset is clamped so removal still can't collapse; re-create those
 * if pixel-perfect height matters. Pre-launch dev data — no full migration.
 */
const LEGACY_GLYPH_PAD = 2;
export function stripLegacyGlyphPad(
  redactions: Redaction[],
  assetFormatVersion: string | undefined
): Redaction[] {
  if (assetFormatVersion !== '0.0.3') {
    return redactions;
  }
  return redactions.map((r) => {
    if (!isGlyphAnchoredRedaction(r)) {
      return r;
    }
    const g = r.geometry;
    return {
      ...r,
      geometry: {
        x: g.x + LEGACY_GLYPH_PAD,
        y: g.y + LEGACY_GLYPH_PAD,
        width: Math.max(1, g.width - 2 * LEGACY_GLYPH_PAD),
        height: Math.max(1, g.height - 2 * LEGACY_GLYPH_PAD),
      },
    };
  });
}

/**
 * Per-side visual pad for a glyph-anchored redaction, capped so the grown box
 * can't overlap an adjacent redaction. For each side we find the nearest
 * redaction that shares the perpendicular extent (a vertical neighbour shares
 * horizontal overlap; a horizontal neighbour shares vertical overlap) and clamp
 * the pad to half the gap to it — so two padded neighbours meet at the midpoint
 * rather than overlapping. Sides with no neighbour get the full max
 * ({@link GLYPH_REDACTION_VISUAL_VPAD} / {@link GLYPH_REDACTION_VISUAL_HPAD}).
 *
 * `neighbours` should be the other rect redactions on the same page (exclude
 * the target itself). Used identically by the editor overlay and the burn-in so
 * the on-screen box matches the burned box.
 */
export function glyphAnchoredVisualPad(
  target: BoundingBox,
  neighbours: BoundingBox[]
): { top: number; bottom: number; left: number; right: number } {
  const tLeft = target.x;
  const tRight = target.x + target.width;
  const tTop = target.y;
  const tBottom = target.y + target.height;
  let gapAbove = Infinity;
  let gapBelow = Infinity;
  let gapLeft = Infinity;
  let gapRight = Infinity;
  for (const n of neighbours) {
    const nLeft = n.x;
    const nRight = n.x + n.width;
    const nTop = n.y;
    const nBottom = n.y + n.height;
    // Vertical neighbour: shares horizontal extent (same column / row stack).
    if (nLeft < tRight && nRight > tLeft) {
      if (nBottom <= tTop) {
        gapAbove = Math.min(gapAbove, tTop - nBottom);
      } else if (nTop >= tBottom) {
        gapBelow = Math.min(gapBelow, nTop - tBottom);
      }
    }
    // Horizontal neighbour: shares vertical extent (same row).
    if (nTop < tBottom && nBottom > tTop) {
      if (nRight <= tLeft) {
        gapLeft = Math.min(gapLeft, tLeft - nRight);
      } else if (nLeft >= tRight) {
        gapRight = Math.min(gapRight, nLeft - tRight);
      }
    }
  }
  const cap = (gap: number, max: number) =>
    Number.isFinite(gap) ? Math.max(0, Math.min(max, gap / 2)) : max;
  return {
    top: cap(gapAbove, GLYPH_REDACTION_VISUAL_VPAD),
    bottom: cap(gapBelow, GLYPH_REDACTION_VISUAL_VPAD),
    left: cap(gapLeft, GLYPH_REDACTION_VISUAL_HPAD),
    right: cap(gapRight, GLYPH_REDACTION_VISUAL_HPAD),
  };
}

/**
 * {@link glyphAnchoredVisualPad} against the other rect redactions on the
 * target's page. Centralizes the neighbour selection — exclude self, same page
 * only, skip custom polygons — so the editor overlay and the burn-in compute
 * the padded box from identical rules (they previously hand-rolled the filter
 * at each call site and could drift). Pass the redaction array each context has;
 * the filtering is handled here.
 *
 * While both pad maxes are 0 this returns a zero pad without reading
 * `pageRedactions` at all — the scan is O(k) per box and its result is
 * discarded, which is O(k²) per page in the burn-in and per render in
 * RedactionOverlay (VE-29483).
 */
export function glyphAnchoredVisualPadAmong(
  target: Redaction,
  pageRedactions: Redaction[]
): { top: number; bottom: number; left: number; right: number } {
  // Both pad maxes are 0 (VE-26506), so every side reads 0 whatever the
  // neighbours are — skip the O(k) neighbour scan. Delete when either goes
  // non-zero.
  if (GLYPH_REDACTION_VISUAL_VPAD === 0 && GLYPH_REDACTION_VISUAL_HPAD === 0) {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }
  const neighbours = pageRedactions
    .filter(
      (o) => o.id !== target.id && o.page === target.page && !o.isCustomPolygon
    )
    .map((o) => o.geometry);
  return glyphAnchoredVisualPad(target.geometry, neighbours);
}

/**
 * The highest page number across the given lists, or 0 when they are all
 * empty. A loop on purpose: `Math.max(...pages)` passes every entry as a call
 * argument and throws `RangeError` past the engine's argument limit (~120k in
 * V8), which a large document's redaction + detection pages reach (VE-29483).
 *
 * NaN entries are skipped (`NaN > n` is false). `Math.max` returned NaN for
 * them instead, which made the export's page-count guard compare false and
 * silently pass; here the guard still sees the real pages.
 */
export function highestPageOf(
  ...lists: ReadonlyArray<ReadonlyArray<number>>
): number {
  let highest = 0;
  for (const list of lists) {
    for (const page of list) {
      if (page > highest) {
        highest = page;
      }
    }
  }
  return highest;
}

/**
 * Drawn region — origin `manual`, whether a plain rectangle or a custom
 * polygon (`isCustomPolygon`). Both are user-drawn free-form regions.
 */
export function isRegionRedaction(r: { origin: RedactionOrigin }): boolean {
  return r.origin === 'manual';
}

/**
 * After deleting text-selection row box(es), re-split the surviving rows of
 * each affected selection so the gap a deleted row leaves SEVERS the group:
 * rows below a deleted row must no longer select together with the rows above
 * it. Without this, deleting a middle line leaves the lines above and below
 * sharing one `textSelectionId`, so clicking either still highlights both
 * across the gap.
 *
 * Each surviving row's "segment" is the number of deleted rows sitting above
 * it (smaller `geometry.y`) within the same page + `textSelectionId`. Segment 0
 * (above every deletion) keeps the original id; each lower segment gets a fresh
 * derived id, so a single delete splits one group into two and multiple deletes
 * split into more. Scoped per page because selection grouping is per page.
 *
 * Mutates the survivors' `textSelectionId` in place. Pure w.r.t. id generation
 * (derived suffix, no RNG) so undo snapshots stay deterministic.
 */
export function resplitTextSelectionsAfterDelete(
  survivors: Redaction[],
  deleted: Redaction[]
): void {
  // Deleted rows' y-cuts, grouped by `${page} ${textSelectionId}`.
  const cutsByGroup = new Map<string, number[]>();
  for (const d of deleted) {
    if (!d.textSelectionId) {
      continue;
    }
    const key = `${d.page} ${d.textSelectionId}`;
    const ys = cutsByGroup.get(key);
    if (ys) {
      ys.push(d.geometry.y);
    } else {
      cutsByGroup.set(key, [d.geometry.y]);
    }
  }
  if (cutsByGroup.size === 0) {
    return;
  }
  // Every id currently in use, so minted ids stay unique even across repeated
  // splits (a `#sN` group can itself be split again later).
  const usedIds = new Set<string>();
  for (const r of survivors) {
    if (r.textSelectionId) {
      usedIds.add(r.textSelectionId);
    }
  }
  const mintId = (root: string): string => {
    let k = 1;
    while (usedIds.has(`${root}#s${k}`)) {
      k += 1;
    }
    const id = `${root}#s${k}`;
    usedIds.add(id);
    return id;
  };
  for (const [key, cutYs] of cutsByGroup) {
    const sep = key.indexOf(' ');
    const page = Number(key.slice(0, sep));
    const oldId = key.slice(sep + 1);
    // Strip any prior `#sN` so a re-split suffixes off the original root.
    const root = oldId.replace(/#s\d+$/, '');
    // One fresh id per distinct below-a-deletion segment; segment 0 (above
    // every deletion) keeps `oldId`.
    const idForSegment = new Map<number, string>();
    for (const r of survivors) {
      if (r.page !== page || r.textSelectionId !== oldId) {
        continue;
      }
      const segment = cutYs.filter((y) => y < r.geometry.y).length;
      if (segment === 0) {
        continue;
      }
      let id = idForSegment.get(segment);
      if (!id) {
        id = mintId(root);
        idForSegment.set(segment, id);
      }
      r.textSelectionId = id;
    }
  }
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * Clamp a box into the page bounds. Result fits within
 * (0,0)..(pageWidth, pageHeight) and never has negative dimensions.
 *
 * Properties:
 * - 0 <= result.x <= pageWidth - result.width
 * - 0 <= result.y <= pageHeight - result.height
 * - result.width >= 0, result.height >= 0
 * - idempotent: clampToPage(clampToPage(b, w, h), w, h) === clampToPage(b, w, h)
 */
export function clampToPage(
  box: BoundingBox,
  pageWidth: number,
  pageHeight: number
): BoundingBox {
  const width = Math.max(0, Math.min(box.width, pageWidth));
  const height = Math.max(0, Math.min(box.height, pageHeight));
  const x = Math.max(0, Math.min(box.x, pageWidth - width));
  const y = Math.max(0, Math.min(box.y, pageHeight - height));
  return { x, y, width, height };
}

/**
 * Enforce minimum dimension. Idempotent.
 */
export function enforceMinDimension(box: BoundingBox): BoundingBox {
  return {
    x: box.x,
    y: box.y,
    width: Math.max(MIN_DIMENSION, box.width),
    height: Math.max(MIN_DIMENSION, box.height),
  };
}

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

/**
 * Apply a resize delta to a box for a given handle.
 *
 * @param box - the box at the start of the drag
 * @param handle - which corner/edge handle is being dragged
 * @param dx - horizontal delta in PDF user-space units
 * @param dy - vertical delta in PDF user-space units
 *
 * Properties:
 * - the anchored corner (opposite the handle) does not move
 * - result respects MIN_DIMENSION via enforceMinDimension
 */
export function applyResize(
  box: BoundingBox,
  handle: ResizeHandle,
  dx: number,
  dy: number
): BoundingBox {
  let { x, y, width, height } = box;

  // Horizontal
  if (handle === 'nw' || handle === 'w' || handle === 'sw') {
    x = box.x + dx;
    width = box.width - dx;
  } else if (handle === 'ne' || handle === 'e' || handle === 'se') {
    width = box.width + dx;
  }

  // Vertical
  if (handle === 'nw' || handle === 'n' || handle === 'ne') {
    y = box.y + dy;
    height = box.height - dy;
  } else if (handle === 'sw' || handle === 's' || handle === 'se') {
    height = box.height + dy;
  }

  // If a drag overshot, collapse to zero at the anchored (opposite) edge
  // instead of flipping the box past it.
  if (width < 0) {
    if (handle === 'nw' || handle === 'w' || handle === 'sw') {
      x = box.x + box.width;
    }
    width = 0;
  }
  if (height < 0) {
    if (handle === 'nw' || handle === 'n' || handle === 'ne') {
      y = box.y + box.height;
    }
    height = 0;
  }

  return enforceMinDimension({ x, y, width, height });
}

/**
 * Compute a bounding box from two corner points.
 */
export function rectFromCorners(
  a: { x: number; y: number },
  b: { x: number; y: number }
): BoundingBox {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const width = Math.abs(a.x - b.x);
  const height = Math.abs(a.y - b.y);
  return { x, y, width, height };
}

/**
 * The x-intervals lying INSIDE the polygon along one horizontal line.
 *
 * NONZERO winding, not even-odd, and the choice is a safety one rather than a
 * stylistic one. Vertices are click-placed, so nothing stops an operator from
 * closing a polygon whose outline crosses itself. Even-odd calls the overlap of
 * two loops OUTSIDE and would skip removing the content there — while the black
 * shape the burn-in worker paints in its Pass 4 is a PDF `Polygon` annotation,
 * whose appearance stream ends in `b` (close/fill/stroke, nonzero — `b*` would
 * be even-odd), so it would cover that same region anyway. That pairing produces
 * the one outcome this pipeline must never produce: content hidden but still in
 * the file. Nonzero can only ever mark MORE as inside than even-odd, so it is
 * also the over-redacting direction.
 *
 * Crossings are counted half-open (`lo <= y < hi`) so the vertex shared by two
 * edges is counted once, not twice; horizontal edges cross nothing and are
 * skipped. A local y-extremum therefore contributes either two coincident
 * crossings (a zero-width span, dropped) or none — never a stray odd one that
 * would invert the rest of the line.
 */
function polygonCrossingsAtY(
  points: ReadonlyArray<{ x: number; y: number }>,
  yLine: number
): Array<{ x: number; dir: number }> {
  const crossings: Array<{ x: number; dir: number }> = [];
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    if (!a || !b || a.y === b.y) {
      continue;
    }
    const lo = Math.min(a.y, b.y);
    const hi = Math.max(a.y, b.y);
    if (yLine < lo || yLine >= hi) {
      continue;
    }
    const t = (yLine - a.y) / (b.y - a.y);
    crossings.push({ x: a.x + t * (b.x - a.x), dir: b.y > a.y ? 1 : -1 });
  }
  return crossings.sort((p, q) => p.x - q.x);
}

function spansFromCrossings(
  crossings: ReadonlyArray<{ x: number; dir: number }>
): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  let winding = 0;
  let spanStart = 0;
  for (const c of crossings) {
    if (winding === 0) {
      spanStart = c.x;
    }
    winding += c.dir;
    if (winding === 0 && c.x > spanStart) {
      spans.push([spanStart, c.x]);
    }
  }
  return spans;
}

/** Sort and coalesce overlapping/touching intervals. Never mutates its input. */
function mergeSpans(
  spans: ReadonlyArray<readonly [number, number]>
): Array<[number, number]> {
  const sorted = [...spans].sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [lo, hi] of sorted) {
    const last = merged[merged.length - 1];
    if (last && lo <= last[1]) {
      // Replace rather than write through: `spans` is only readonly at the
      // array level, so `last[1] = ...` would reach into the caller's tuples.
      merged[merged.length - 1] = [last[0], Math.max(last[1], hi)];
    } else {
      merged.push([lo, hi]);
    }
  }
  return merged;
}

/**
 * The y values where the polygon's horizontal cross-section can change SHAPE —
 * every vertex, plus every point where two edges cross.
 *
 * These are what make {@link polygonToStrips}'s per-sub-band pairing sound.
 * Between two consecutive events the span count is constant and the spans
 * cannot change left-to-right order (a count change needs a vertex; a reorder
 * needs an edge crossing), so span *k* at the top of a sub-band and span *k* at
 * the bottom are the same piece of the polygon, and the interval between them is
 * bounded by their endpoints. Cross an event and that correspondence breaks.
 *
 * O(n²) in the vertex count, computed once per polygon — vertices are placed by
 * hand, so n is small.
 */
function polygonEventYs(
  points: ReadonlyArray<{ x: number; y: number }>
): number[] {
  const ys = points.map((p) => p.y);
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    if (!a || !b) {
      continue;
    }
    for (let j = i + 1; j < points.length; j++) {
      const c = points[j];
      const d = points[(j + 1) % points.length];
      if (!c || !d) {
        continue;
      }
      const rx = b.x - a.x;
      const ry = b.y - a.y;
      const sx = d.x - c.x;
      const sy = d.y - c.y;
      const denom = rx * sy - ry * sx;
      if (denom === 0) {
        continue; // parallel or collinear — no single crossing y
      }
      const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / denom;
      const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / denom;
      if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
        ys.push(a.y + t * ry);
      }
    }
  }
  return ys;
}

/**
 * Decompose a free-form polygon into a stack of axis-aligned rectangles
 * ("scanline strips") that cover it. MuPDF's `applyRedactions` ignores a
 * redaction annotation's QuadPoints and only honors its bounding Rect, so a
 * true polygon redaction is burned in as the union of these strip rects (each
 * a real Redact annotation that strips underlying content).
 *
 * Each band emits one strip PER INTERIOR SPAN, not one strip across the
 * polygon's whole x-extent. The difference is everything on a concave shape: a
 * single span per band swallows the notch of a U or an L, and since the removal
 * pass paints nothing (`black_boxes=false`) while the worker's Pass 4 paints
 * only the true outline, the swallowed area came out as deleted-but-unpainted
 * content — a white hole in the burned-in page, and real over-redaction of text
 * the operator never selected.
 *
 * Coverage is a SUPERSET of the polygon, and that is a guarantee rather than a
 * hope — under-covering would leave sensitive content alive underneath the
 * black shape Pass 4 paints, the one outcome this pipeline must never produce.
 * It holds because each band is cut at every {@link polygonEventYs} value it
 * contains, and within a resulting sub-band span *k* is the same piece of the
 * polygon at the top and at the bottom. A span's edges are straight, so x varies
 * linearly in y, so the whole span over that sub-band is bounded by
 * `[min(left), max(right)]` of its two ends — the strip we emit. Sampling the
 * two ends and UNIONING them instead would be a heuristic, and a wrong one: a
 * thin shape that slides sideways faster than its own width leaves everything
 * between the two samples uncovered.
 *
 * That correspondence holds strictly BETWEEN events, so both samples are taken
 * strictly inside the sub-band. Sampling an end ON its own boundary reads a
 * degenerate cross-section — an apex there contributes a zero-width span that
 * the winding walk drops, and a self-touch splits one span into two. Let those
 * cancel and the count still matches, the fallback never fires, and span *k* is
 * hulled against a different piece of the polygon. That is an uncovered gap, and
 * it is reachable: vertices are click-placed, so two clicks on one screen row
 * tie exactly.
 *
 * Where the two ends disagree on span count — numerically, or at a degenerate
 * touch — the sub-band falls back to one strip bracketing everything either end
 * saw. That over-covers, which is the safe direction.
 *
 * The remaining tradeoff is staircased edges (finer `stripHeight` → smoother),
 * which also errs toward over-redaction.
 *
 * @param points polygon vertices in PDF user-space (top-left origin)
 * @param stripHeight band height in PDF units (smaller = smoother, more rects)
 */
export function polygonToStrips(
  points: ReadonlyArray<{ x: number; y: number }>,
  stripHeight: number
): BoundingBox[] {
  if (points.length < 3 || stripHeight <= 0) {
    return [];
  }
  // Outward safety pad (PDF units) so the strip union always fully covers the
  // polygon edge and neighbouring strips overlap (no hairline gaps).
  const STRIP_PAD = 0.3;
  const ys = points.map((p) => p.y);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  if (!(maxY > minY)) {
    return []; // zero height — covers nothing
  }
  // Nudge used to pull both samples off their sub-band's boundaries, where the
  // cross-section is degenerate and the span correspondence can break. Also what
  // stops the final sub-band (`yc === maxY`, where the half-open rule says
  // nothing is inside) coming back empty. Small enough that the x it shifts a
  // sample by is under STRIP_PAD for any slope short of 3e8; a near-horizontal
  // edge is self-limiting anyway, since its endpoints are events and the
  // sub-band containing it is therefore no longer than the edge.
  const SAMPLE_EPS = 1e-9;
  const events = polygonEventYs(points)
    .filter((y) => y > minY && y < maxY)
    .sort((a, b) => a - b);

  const rects: BoundingBox[] = [];
  const emit = (ya: number, yc: number) => {
    // BOTH ends strictly inside — see the note on degenerate cross-sections in
    // this function's docstring. `ya` and `yc` are event values by construction
    // (the first band starts at `minY`; every cut is an event), so sampling
    // either one exactly is sampling the one place the span correspondence is
    // allowed to break.
    const topCrossings = polygonCrossingsAtY(
      points,
      Math.min(ya + SAMPLE_EPS, yc)
    );
    const bottomCrossings = polygonCrossingsAtY(
      points,
      Math.max(ya, yc - SAMPLE_EPS)
    );
    const top = spansFromCrossings(topCrossings);
    const bottom = spansFromCrossings(bottomCrossings);

    let spans: Array<[number, number]>;
    if (top.length === bottom.length && top.length > 0) {
      spans = top.map((s, i) => {
        const other = bottom[i] ?? s;
        return [Math.min(s[0], other[0]), Math.max(s[1], other[1])];
      });
    } else {
      // The two ends disagree on how many spans there are, so span *k* at one
      // end is not span *k* at the other and pairing would be guesswork.
      // Bracket every x this sub-band can reach instead — including its
      // vertices, which is the part that is easy to forget: an apex contributes
      // a ZERO-WIDTH span that the winding walk drops, so its x reaches this
      // fallback through nothing else, and the gap opens directly beneath it.
      const xs = [
        ...topCrossings.map((c) => c.x),
        ...bottomCrossings.map((c) => c.x),
      ];
      for (const p of points) {
        if (p.y >= ya && p.y <= yc) {
          xs.push(p.x);
        }
      }
      spans = xs.length > 0 ? [[Math.min(...xs), Math.max(...xs)]] : [];
    }

    for (const [lo, hi] of mergeSpans(spans)) {
      if (hi <= lo) {
        continue; // covers nothing
      }
      // Expand outward by a small pad (never floor to integers — that added
      // up to a full unit of extra jaggedness) so the union of strips still
      // fully covers the polygon while following its edges precisely. The
      // vertical pad also overlaps neighbouring strips so no hairline gap
      // shows between them.
      rects.push({
        x: lo - STRIP_PAD,
        y: ya,
        width: hi - lo + 2 * STRIP_PAD,
        height: yc - ya + STRIP_PAD,
      });
    }
  };

  let eventIdx = 0;
  for (let yt = minY; yt < maxY; yt += stripHeight) {
    const yb = Math.min(yt + stripHeight, maxY);
    // Cut this band at every event inside it, so each piece we emit spans a
    // stretch where the cross-section keeps its shape.
    let ya = yt;
    while (eventIdx < events.length && (events[eventIdx] ?? maxY) < yb) {
      const cut = events[eventIdx] ?? yb;
      eventIdx++;
      if (cut > ya) {
        emit(ya, cut);
        ya = cut;
      }
    }
    if (yb > ya) {
      emit(ya, yb);
    }
  }
  return rects;
}

// ---------------------------------------------------------------------------
// Detections
// ---------------------------------------------------------------------------

/**
 * Build the override key used by `detectionRectOverrides`.
 */
export function detectionRectKey(
  detectionId: string,
  page: number,
  rectIdx: number
): DetectionRectKey {
  return `${detectionId}|${page}|${rectIdx}`;
}

/**
 * Resolve the effective rect for a given detection occurrence rect:
 * if there is an override, return it; otherwise the engine's geometry.
 *
 * Idempotent.
 */
export function mergeDetectionAndOverride(
  occurrenceRect: BoundingBox,
  override: BoundingBox | undefined
): BoundingBox {
  return override ?? occurrenceRect;
}

// ---------------------------------------------------------------------------
// Redaction Plan (build the burn-in payload from session state)
// ---------------------------------------------------------------------------

export interface RedactionPlanItem {
  page: number;
  geometry: BoundingBox;
  origin: 'auto' | 'manual' | 'keyword' | 'page-redact' | 'text-select';
  /**
   * Free-form polygon vertices (custom-polygon `manual` items). When present
   * (≥3), burn-in decomposes these into axis-aligned redaction rectangles;
   * `geometry` stays the polygon's bounding box for non-polygon-aware
   * consumers. Absent → the item is a plain rectangle (`geometry`).
   */
  points?: { x: number; y: number }[];
  redactionCode?: { id: string; label: string; color: string };
  /**
   * True when `geometry` is a NEUTRAL glyph quad rather than a hand-drawn or
   * vendor-supplied box (VE-24948).
   *
   * `origin` cannot answer this. Keyword and text-select redactions are
   * glyph-anchored and carry `origin: 'keyword' | 'text-select'`, but a lexicon
   * detection is glyph-anchored and lands here as `origin: 'auto'` — the same
   * origin as a PII-engine detection, whose box comes from the vendor and is
   * NOT a glyph quad. Burn-in has to tell them apart: MuPDF's per-glyph quad is
   * the full font line box (ascent to descent, see the note above
   * `GLYPH_REDACTION_REMOVAL_VINSET`), so removing content under it without the
   * vertical inset deletes glyphs from the rows above and below.
   */
  glyphAnchored?: boolean;
}

export interface RedactionPlan {
  pages: number[];
  items: RedactionPlanItem[];
}

/**
 * Combine checked detections, manual redactions, keyword redactions, and
 * page-redacts into a single deterministic plan ready for export.
 *
 * Round-trip: serialize/deserialize via JSON yields a structurally equal plan.
 */
export function buildRedactionPlan(session: SessionState): RedactionPlan {
  const items: RedactionPlanItem[] = [];

  // 1. Redacted auto-detections. When a detection carries vendor match ids we
  // gate per rect on its `redactedMatchIds` set (so a single redacted match on
  // a page burns in without its siblings); otherwise we fall back to the
  // page-level checked / checkedPages path for legacy detections.
  for (const det of session.detections) {
    const perMatch = hasMatchIds(det);
    const redactedSet = new Set(det.redactedMatchIds ?? []);
    const isChecked = det.checked === true;
    const partialPages =
      det.checked === 'partial' ? (det.checkedPages ?? []) : [];

    for (const occ of det.occurrences) {
      if (!perMatch) {
        const includeWholeDetection = isChecked;
        const includePartialPage = partialPages.includes(occ.page);
        if (!includeWholeDetection && !includePartialPage) {
          continue;
        }
      }
      occ.rects.forEach((rect, rectIdx) => {
        if (perMatch) {
          const matchId = occ.matchIds?.[rectIdx];
          if (!matchId || !redactedSet.has(matchId)) {
            return;
          }
        }
        const override =
          session.detectionRectOverrides[
            detectionRectKey(det.id, occ.page, rectIdx)
          ];
        items.push({
          page: occ.page,
          geometry: mergeDetectionAndOverride(rect, override),
          origin: 'auto',
          redactionCode: det.redactionCode,
          // Lexicon rows are glyph-anchored; PII-engine rows on this same
          // `origin: 'auto'` path are not. See `glyphAnchored` on the item.
          ...(isLexiconDetection(det) ? { glyphAnchored: true } : {}),
        });
      });
    }
  }

  // 2. Manual + keyword redactions. VE-20839: un-redacted records stay
  // in session state for the user to re-enable but skip burn-in.
  for (const r of session.redactions) {
    if (r.redacted === false) {
      continue;
    }
    items.push({
      page: r.page,
      geometry: r.geometry,
      origin: r.origin === 'page-redact' ? 'page-redact' : r.origin,
      points: r.points,
      redactionCode: r.redactionCode,
    });
  }

  // 3. Page-redacts (just the page numbers; burn-in expands to full-page rects)
  // Keeping a separate `pages` field so the burn-in engine can apply page-level
  // redaction without us computing the page bounds here.
  const pages = [...new Set(session.pageRedacts)].sort((a, b) => a - b);

  // Stable ordering: sort items by page, then origin, then geometry x/y.
  items.sort((a, b) => {
    if (a.page !== b.page) {
      return a.page - b.page;
    }
    if (a.origin !== b.origin) {
      return a.origin.localeCompare(b.origin);
    }
    if (a.geometry.x !== b.geometry.x) {
      return a.geometry.x - b.geometry.x;
    }
    return a.geometry.y - b.geometry.y;
  });

  return { pages, items };
}

// ---------------------------------------------------------------------------
// Persistence (auto-save payload <-> session)
// ---------------------------------------------------------------------------

/**
 * Project a SessionState into the auto-save payload shape.
 */
export function buildPersistedRedactionState(
  session: SessionState
): PersistedRedactionState {
  return {
    schemaVersion: 1,
    redactions: session.redactions,
    detectionRectOverrides: session.detectionRectOverrides,
    pageRedacts: session.pageRedacts,
    excludedPages: session.excludedPages,
    detectionState: session.detections.map((d) => ({
      id: d.id,
      checked: d.checked,
      checkedPages: d.checkedPages,
      redactionCode: d.redactionCode,
    })),
    // Per-match PII records (flattened; bucketed per page in the asset).
    piiMatches: buildPiiMatches(session.detections),
    // VE-24447. `undefined` rather than `null` when unset: this shape is the
    // optional-key wire format, and the export saga reads it as "is there a
    // stamp?" either way.
    batesStamp: session.batesStamp ?? undefined,
    // Lexicon additions (VE-24948 / U5). Emitted only when present so a
    // document that has never had a lexicon applied projects exactly as it did
    // before U5 (BR-52 / BR-53). Note this is a SPREAD rather than an
    // always-present `?? undefined` like the line above — the two are not
    // interchangeable here: BR-52 requires the key to be absent, not merely
    // undefined, so the body is byte-identical to a pre-U5 build's.
    ...(session.appliedLexicons.length > 0
      ? { appliedLexicons: session.appliedLexicons }
      : {}),
    ...(session.detections.some(isLexiconDetection)
      ? { lexiconDetections: session.detections.filter(isLexiconDetection) }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Persisted asset format (VE-20835)
//
// In-memory `Redaction[]` is a flat list of { id, origin, page, geometry }.
// The persisted asset body groups by page, then by origin, keyed by
// redaction id. Coordinates are written with fixed precision so reviews
// diff cleanly and asset files stay small.
//
// `manual`, `keyword`, and `text-select` origins are persisted. Every entry
// stores its outline as `points`: a rectangle writes its 4 corners, a custom
// `manual` polygon (`isCustomPolygon`) writes its vertices. The bounding box
// is derived from `points` on load, never stored.
// ---------------------------------------------------------------------------

type PersistedOrigin = Extract<
  RedactionOrigin,
  'manual' | 'keyword' | 'text-select'
>;
const PERSISTED_ORIGINS: readonly PersistedOrigin[] = [
  'manual',
  'keyword',
  'text-select',
];

function round(value: number): number {
  return Number(value.toFixed(DOCUMENT_REDACTION_COORD_PRECISION));
}

/**
 * The four corners of an axis-aligned box, clockwise from top-left
 * (TL, TR, BR, BL). This is how a rectangle redaction serializes its
 * `points` — the canonical outline, from which the box is re-derived on load.
 */
function boxToPoints(box: BoundingBox): PersistedPoint[] {
  const x1 = round(box.x);
  const y1 = round(box.y);
  const x2 = round(box.x + box.width);
  const y2 = round(box.y + box.height);
  return [
    [x1, y1],
    [x2, y1],
    [x2, y2],
    [x1, y2],
  ];
}

/**
 * Per-point shape check. The persisted JSON could be hand-edited, a stale
 * schema, or partially corrupt — return false for anything that isn't a
 * `[number, number]` tuple so the caller can skip the entry instead of
 * crashing on `pointsToBox`.
 */
function isValidPoint(p: unknown): p is PersistedPoint {
  return (
    Array.isArray(p) &&
    p.length === 2 &&
    typeof p[0] === 'number' &&
    Number.isFinite(p[0]) &&
    typeof p[1] === 'number' &&
    Number.isFinite(p[1])
  );
}

/**
 * A valid persisted outline: an array of ≥3 valid points (a rectangle is 4,
 * the smallest polygon is 3).
 */
function isValidPoints(points: unknown): points is PersistedPoint[] {
  return (
    Array.isArray(points) && points.length >= 3 && points.every(isValidPoint)
  );
}

/** Axis-aligned bounding box of an outline — the in-memory `geometry`. */
function pointsToBox(points: PersistedPoint[]): BoundingBox {
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Convert the in-memory `Redaction[]` into the persisted asset body.
 * Skips `page-redact` origin (not in scope per the ticket); the saved set
 * is the subset of redactions whose origin is in `PERSISTED_ORIGINS`.
 */
function isPersistedOrigin(origin: RedactionOrigin): origin is PersistedOrigin {
  return (PERSISTED_ORIGINS as readonly RedactionOrigin[]).includes(origin);
}

/**
 * Everything that goes into a document's redaction-data asset.
 *
 * An OBJECT, not positional parameters. This had grown to seven positionals of
 * which five defaulted and four were same-typed arrays, so omitting one — or
 * transposing two — compiled cleanly and silently wrote an asset missing data
 * the editor was still showing. That is not hypothetical: VE-28061 was exactly
 * that bug, when this helper was lifted out of the autosave loop and the
 * `watermarks` argument did not come with it, and every applied watermark
 * vanished from the saved file. The same shape was refactored away from
 * `burnInPdf` for the same reason (see `BurnInOptions`); this is the other half.
 *
 * Only `redactions` and `tdoId` are required; every other field is optional and
 * absent means empty.
 */
export interface RedactionAssetBodyInput {
  redactions: Redaction[];
  tdoId: TDOId;
  /** Pages dropped from the burned-in output ("Delete Page"). */
  excludedPages?: number[];
  /** Pages blanked to a slip sheet ("Remove Page"). */
  pageRedacts?: number[];
  /**
   * `Detection`, not `PiiDetection` — the list carries LEXICON cards too
   * (VE-24948), and narrowing it here would make them invisible to the
   * serializer. `buildPiiMatches` and the lexicon projection each narrow for
   * themselves further down.
   */
  detections?: readonly Detection[];
  watermarks?: AppliedWatermark[];
  watermarksOff?: boolean;
  batesStamp?: BatesStampSettings | null;
  batesStampOff?: BatesStampSettings | null;
  /**
   * What `watchFirstOpen` reads to decide a document has already been
   * evaluated (VE-24948). Dropping it makes every reopen re-run a full apply
   * and autosave a new version with no user action (review-gate B5) — the same
   * class of silent loss the object shape above exists to prevent.
   */
  appliedLexicons?: readonly AppliedLexiconRecord[];
}

export function redactionsToAssetBody({
  redactions,
  tdoId,
  excludedPages = [],
  pageRedacts = [],
  detections = [],
  watermarks = [],
  watermarksOff = false,
  batesStamp = null,
  batesStampOff = null,
  appliedLexicons = [],
}: RedactionAssetBodyInput): DocumentRedactionAssetV1 {
  const byPage: DocumentRedactionAssetV1['redactions'] = {};
  for (const r of redactions) {
    if (!isPersistedOrigin(r.origin)) {
      continue;
    }
    // VE-20838: redactions stuck to every page serialize under the
    // literal `"all"` key so load can mark them appliesToAllPages=true
    // on the way back in. The redaction's own `page` field is
    // discarded for these — they're not anchored to a specific page.
    const pageKey = r.appliesToAllPages ? 'all' : String(r.page);
    const pageBucket = (byPage[pageKey] ??= {});
    const originBucket = (pageBucket[r.origin] ??= {});
    // Geometry persists as `points` only — the canonical outline. A custom
    // polygon writes its vertices; a rectangle (everything else) writes its
    // four corners. The bounding box is NOT stored; it's re-derived on load.
    const isPolygon = !!r.isCustomPolygon && !!r.points && r.points.length >= 3;
    const entry: PersistedRedactionEntry = {
      points: isPolygon
        ? r.points!.map((p) => [round(p.x), round(p.y)])
        : boxToPoints(r.geometry),
    };
    if (isPolygon) {
      entry.isCustomPolygon = true;
    }
    if (r.redactionCode) {
      entry.redactionCode = r.redactionCode;
    }
    // VE-20839: persist `redacted: false` only — the default (redacted /
    // undefined) stays omitted so existing assets round-trip
    // byte-identical for readers that don't know about this field.
    if (r.redacted === false) {
      entry.redacted = false;
    }
    // NB: provenance ("Keyword" vs "Text") is no longer persisted as a
    // separate `selectionMode` — the origin bucket key this entry lands in
    // (`keyword` vs `text-select`) IS the source of truth and round-trips it.
    // VE-20839: persist the display name for Region cards. Only emitted when
    // set, so older assets stay byte-identical for legacy readers.
    if (r.name) {
      entry.name = r.name;
    }
    // VE-20839: persist `phrase` on every keyword / text-select
    // redaction. The original word-count cap was a privacy hedge, but
    // the geometry already pins the redacted region down in the doc
    // so leaving `phrase` out doesn't actually hide anything from
    // anyone holding the source PDF — it just costs the panel its
    // preview text after reload. Drop the cap so long selections
    // also round-trip; the card title is 3-line-clamped in the UI.
    if (r.phrase && (r.origin === 'keyword' || r.origin === 'text-select')) {
      const trimmed = r.phrase.trim();
      if (trimmed) {
        entry.phrase = trimmed;
      }
    }
    // Persist the row-group id so clicking one row still selects the whole
    // stretch of text after reload. Carried by text-select boxes (one id per
    // selection gesture) AND by keyword boxes whose match wraps a line
    // (VE-26267) — origin-agnostic on purpose, so both round-trip.
    if (r.textSelectionId) {
      entry.textSelectionId = r.textSelectionId;
    }
    // Asset format 1.0.0: persist per-row word count on text-select entries so
    // word-count metrics can be totaled without re-deriving from `phrase`
    // (which may be re-stamped on merge). Computed from the row's phrase.
    if (r.origin === 'text-select') {
      entry.wordCount = r.wordCount ?? countWords(entry.phrase);
    }
    originBucket[r.id] = entry;
  }
  // Stamp the "Delete Page" flag on each deleted page bucket. If the page had no
  // other redactions, the bucket gets created here just to carry the flag —
  // round-trip readers will see an entry with no `manual` or `keyword` map and
  // just the marker. Asset format 1.1.0 writes `pageHidden`; the legacy
  // `pageRemoved` is written too so a pre-1.1.0 build still honors it.
  for (const page of excludedPages) {
    const pageKey = String(page);
    const pageBucket = (byPage[pageKey] ??= {});
    pageBucket.pageHidden = true;
    pageBucket.pageRemoved = true;
  }
  // Likewise for "Remove Page" (the FOIA slip-sheet). Persisting as a bucket
  // flag — not as a full-page manual redaction — keeps the data model honest:
  // the burn-in worker blanks the page and stamps the notice instead of
  // treating it like a black rectangle that happens to span the page.
  for (const page of pageRedacts) {
    const pageKey = String(page);
    const pageBucket = (byPage[pageKey] ??= {});
    pageBucket.pageContentRemoved = true;
    pageBucket.pageFullyRedacted = true;
  }
  // Per-match PII records bucketed under each page's `PII` array — the active
  // PII-selection mechanism.
  for (const m of buildPiiMatches(detections)) {
    const pageBucket = (byPage[String(m.page)] ??= {});
    (pageBucket.PII ??= []).push(m);
  }
  // Lexicon detections (VE-24948 / U5): cards at the root, matches bucketed per
  // page beside the PII records.
  const lexicon = buildLexiconAssetRecords(detections);
  for (const [pageKey, entries] of Object.entries(lexicon.matchesByPage)) {
    const pageBucket = (byPage[pageKey] ??= {});
    (pageBucket.LEXICON ??= []).push(...entries);
  }
  // Whether this body carries ANY of the three shapes U5 added: the per-page
  // LEXICON bucket, the root detections, or the applied records.
  const hasLexiconData =
    lexicon.detections.length > 0 ||
    appliedLexicons.length > 0 ||
    Object.keys(lexicon.matchesByPage).length > 0;
  const body: DocumentRedactionAssetV1 = {
    // CONDITIONAL, and that is BR-52/53 rather than an optimisation.
    // A document that never had a lexicon applied must serialize
    // byte-identically to a save from a build without this feature, and the
    // version field is part of the bytes. Stamping the lexicon version
    // unconditionally also destroyed the only rollback triage signal there is:
    // `details.assetFormatVersion` exists so an operator can tell WHICH
    // documents would lose data on a rollback below the U5 build, and it
    // cannot do that if every document reports the new format (review-gate R7).
    //
    // The watermark work landed the same way, but it does NOT need the same
    // treatment: a watermark-free document omits the `watermarks` key and
    // stamps the PRE_LEXICON version, which tracks the highest non-lexicon
    // format. So the two features stay independent — see `store.ts`.
    assetFormatVersion: hasLexiconData
      ? DOCUMENT_REDACTION_ASSET_FORMAT_VERSION
      : DOCUMENT_REDACTION_ASSET_FORMAT_VERSION_PRE_LEXICON,
    tdoId,
    redactions: byPage,
    // Omitted entirely when unused, so a document that has never had a lexicon
    // applied serializes byte-identically to a pre-U5 save (BR-53).
    ...(lexicon.detections.length > 0
      ? { lexiconDetections: lexicon.detections }
      : {}),
    ...(appliedLexicons.length > 0
      ? { appliedLexicons: appliedLexicons.map(normalizeAppliedLexicon) }
      : {}),
  };
  // VE-24693: persist watermarks at the top level (they burn onto every page).
  // Only emitted when present so watermark-free docs round-trip byte-identical.
  //
  // The DRAWN image (`imageDataUrl`) is deliberately not persisted. It is a pure
  // function of `imageBaseDataUrl` + `grayscale` + `color`
  // (`deriveWatermarkImage`), so storing it alongside its own inputs doubled the
  // payload — ~2.7MB of base64 per copy per image mark, re-uploaded on every
  // autosave and retained across five versions, and copied into every document
  // a bulk apply touches. It was also a second source of truth: the export reads
  // the drawn form, so a cached copy that fell out of step with the flags would
  // burn in the wrong image. It is derived again when the document opens.
  if (watermarks.length > 0) {
    body.watermarks = watermarksForStorage(watermarks);
  }
  // Only when OFF, so a document with the switch on round-trips byte-identical.
  if (watermarksOff) {
    body.watermarksOff = true;
  }
  // VE-24447: same treatment — top level (it stamps every page), and only
  // emitted when set, so a document without Bates numbering round-trips
  // byte-identical.
  if (batesStamp) {
    body.batesStamp = batesStamp;
  } else if (batesStampOff) {
    // Only when numbering is off; the reducer keeps the two exclusive, and the
    // `else` holds that on the wire even if something else did not.
    body.batesStampOff = batesStampOff;
  }
  return body;
}

/**
 * Strip the derived image from every watermark on its way to storage.
 *
 * Extracted so the bulk-apply path in `casemedia` cannot skip it. It did: that
 * saga hand-assembled the asset body and wrote `appliedFromDefinition`'s output
 * verbatim, which carries the drawn PNG *and* its base — so the one path that
 * multiplies a watermark across every selected file was also the one path that
 * stored it twice, ~5.4 MB per document for a 2 MB logo.
 */
export function watermarksForStorage(
  watermarks: readonly AppliedWatermark[]
): AppliedWatermark[] {
  return watermarks.map((wm) => {
    if (wm.type !== 'image') {
      return wm;
    }
    // The drawn form is always dropped: it is a pure function of the base plus
    // the flags, and it is what made the body unreadable.
    //
    // The BASE is dropped only when `imageAssetId` actually points at the asset
    // that replaces it. Dropping it unconditionally was a silent, permanent
    // data loss: an upload that failed (swallowed to a console warning), a
    // 1.2.0 body whose inline base nothing ever uploads, a save inside the
    // upload window, or an undo across it all produce a mark with no id — and
    // the body then carried no artwork and no way to find any. Worse, it was
    // invisible: the export in that session still burned in from the in-memory
    // raster, so the damage only appeared on the next open.
    //
    // Keeping the base in that case costs the pre-1.3.0 payload for a mark that
    // would otherwise be lost outright, and it self-heals — the next successful
    // upload drops it again.
    return wm.imageAssetId
      ? { ...wm, imageDataUrl: null, imageBaseDataUrl: null }
      : { ...wm, imageDataUrl: null };
  });
}

/**
 * Append watermarks to a document's redaction asset (VE-24693, AC#10).
 *
 * The single writer for the bulk-apply path, exported so `casemedia` calls into
 * the module that owns this schema instead of assembling it. Two things went
 * wrong while it did: the no-prior-asset fallback hardcoded `'1.0.0'` while the
 * constant said `'1.1.0'` — stamping a 1.1.0 feature under a 1.0.0 version —
 * and it skipped {@link watermarksForStorage}. Same asset key written two ways
 * depending on which surface applied the mark.
 *
 * Adds to whatever the document already carries rather than replacing it, and
 * `existing` is the body just read back from the asset.
 *
 * "Add if absent", not a blind append: a library watermark already on the
 * document is skipped, keyed on `watermarkId`. Bulk apply means "make sure
 * these marks are on these files", and running it twice — or over a selection
 * that overlaps the last one — used to stamp a second identical copy on every
 * document and grow the asset each time, with nothing in the UI to suggest it
 * had. In-editor duplicates are unaffected: those are added through the editor's
 * own action and carry no `watermarkId` from this path.
 */
export function appendWatermarksToAssetBody(
  existing: DocumentRedactionAssetV1 | undefined,
  tdoId: TDOId,
  watermarks: readonly AppliedWatermark[]
): DocumentRedactionAssetV1 {
  const base: DocumentRedactionAssetV1 = existing ?? {
    // PRE_LEXICON, not the lexicon version: this branch INVENTS a body for a
    // document with no asset yet, so it cannot contain lexicon data, and
    // stamping the lexicon format would break the BR-52/53 property that only
    // documents actually carrying lexicon rows report it. When `existing` is
    // supplied the spread below keeps whatever version it already had, which is
    // what preserves 1.4.0 on a document that does.
    assetFormatVersion: DOCUMENT_REDACTION_ASSET_FORMAT_VERSION_PRE_LEXICON,
    tdoId,
    redactions: {},
  };
  const current = watermarksFromAssetBody(base);
  const alreadyApplied = new Set(
    current.map((w) => w.watermarkId).filter((id): id is string => !!id)
  );
  const toAdd = watermarks.filter(
    (w) => !w.watermarkId || !alreadyApplied.has(w.watermarkId)
  );
  return {
    ...base,
    tdoId,
    watermarks: watermarksForStorage([...current, ...toAdd]),
  };
}

/**
 * Read Bates settings back out of an asset body (VE-24447).
 *
 * Validates rather than trusting the shape: this body is user-editable object
 * storage, it is read on every open, and a malformed record must degrade to "no
 * stamp" instead of throwing on the load path or reaching the burn-in worker
 * and producing "NaN" on every page of a released record.
 */
export function batesStampFromAssetBody(
  asset: DocumentRedactionAssetV1
): BatesStampSettings | null {
  return batesSettingsFromRaw((asset as { batesStamp?: unknown }).batesStamp);
}

/**
 * The Bates settings kept while numbering is off (`batesStampOff`), validated
 * exactly like the live stamp. Null when numbering is on — a hand-edited body
 * carrying both reads as numbered, which is what the export would do.
 */
export function batesStampOffFromAssetBody(
  asset: DocumentRedactionAssetV1
): BatesStampSettings | null {
  if (batesStampFromAssetBody(asset)) {
    return null;
  }
  return batesSettingsFromRaw(
    (asset as { batesStampOff?: unknown }).batesStampOff
  );
}

function batesSettingsFromRaw(raw: unknown): BatesStampSettings | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const b = raw as Partial<BatesStampSettings>;
  if (
    typeof b.digits !== 'number' ||
    typeof b.startNumber !== 'number' ||
    typeof b.fontSize !== 'number'
  ) {
    return null;
  }
  const anchorH =
    b.anchorH === 'left' || b.anchorH === 'right' || b.anchorH === 'center'
      ? b.anchorH
      : 'right';
  const anchorV = b.anchorV === 'top' ? 'top' : 'bottom';
  // EVERY field goes through the one shared clamp — including `color`, which an
  // earlier version let through as any string. That string reaches an emotion
  // `sx` colour in the on-canvas preview, and emotion does not escape, so a
  // hand-edited asset could inject CSS into the reviewer's editor (the app
  // serves no CSP). The worker was never at risk; the preview was.
  //
  // This is the trust boundary: the modal's validation is a widget control, and
  // this record comes out of hand-editable object storage on every open.
  // Clamping beats rejecting: a slightly-off stamp the operator can see and
  // correct is better than silently losing the whole sequence on a document
  // that already had one.
  return clampBatesSettings({
    digits: b.digits,
    startNumber: b.startNumber,
    prefix: b.prefix,
    suffix: b.suffix,
    anchorH,
    anchorV,
    fontSize: b.fontSize,
    color: b.color,
    bold: b.bold,
    insetXPt: b.insetXPt,
    insetYPt: b.insetYPt,
  });
}

/** Every field a stored image mark can carry its artwork, or a pointer, in. */
const ARTWORK_KEYS = [
  'imageDataUrl',
  'imageBaseDataUrl',
  'imageGrayscaleDataUrl',
  'imageAssetId',
] as const;

/**
 * Read persisted watermarks (VE-24693) from a redaction-data asset. Defensive:
 * returns [] for a legacy/watermark-free asset or a malformed `watermarks`
 * field, so an older or partial asset still opens.
 *
 * An image mark that ends up with NEITHER a raster nor an asset id is dropped
 * (VE-28770). It is not a watermark — there is nothing to draw, nothing to
 * fetch, and `toBurnInWatermark` already discards it, so the document exports
 * exactly the same either way. What it did do was occupy a row in the watermark
 * panel, which told the operator a mark was applied when none was.
 *
 * Auto-apply wrote these before it learned to resolve artwork from the
 * definition's TDO. They cannot be repaired from the body alone: the raster was
 * never stored and no id points at one. Recovering it from the library through
 * `watermarkId` is possible and deliberately not done — see the ticket.
 *
 * Dropped rather than flagged because the honest reading of such a mark is that
 * the document has no watermark, which is also what the export has always said.
 * It is dropped AFTER normalising, not before: `imageBaseDataUrl` here is the
 * sanitised value, so a row whose inline raster is some other client's non-PNG
 * is correctly read as having nothing rather than as having artwork.
 */
/** Whether the saved document has its watermarks switched off. */
export function watermarksOffFromAssetBody(
  asset: DocumentRedactionAssetV1
): boolean {
  return (asset as { watermarksOff?: unknown }).watermarksOff === true;
}

export function watermarksFromAssetBody(
  asset: DocumentRedactionAssetV1
): AppliedWatermark[] {
  const raw = (asset as { watermarks?: unknown }).watermarks;
  if (!Array.isArray(raw)) {
    return [];
  }
  const valid = raw.filter(
    (w): w is AppliedWatermark =>
      !!w &&
      typeof w === 'object' &&
      typeof (w as AppliedWatermark).instanceId === 'string' &&
      ((w as AppliedWatermark).type === 'text' ||
        (w as AppliedWatermark).type === 'image')
  );
  // Kept beside the raw entries it came from, index for index, because the drop
  // below has to ask what the STORED row carried as well as what survived
  // normalising.
  const marks = valid.map((w) => {
    // Anchor, inset and bold arrive through `...w` like everything else, and
    // like everything else in this body they are writable through the public
    // API. Narrowed HERE rather than trusted: an unrecognised anchor string
    // would flow into `anchoredCenterPt`, miss both edge branches and land on
    // the centre fallback, so a mark saved at bottom-right would silently
    // reappear centred — the failure mode the caller can least see, because
    // the mark is still drawn.
    const anchored = {
      anchorH: isAnchorH(w.anchorH) ? w.anchorH : 'center',
      anchorV: isAnchorV(w.anchorV) ? w.anchorV : 'middle',
      inset: clampWatermarkInset(w.inset),
      bold: w.bold === true,
      align: isTextAlign(w.align) ? w.align : 'center',
      // Only as a PAIR: half a custom position is not a position, and the
      // resolver would fall through to the anchor for one of the two axes.
      ...(typeof w.customXPct === 'number' && typeof w.customYPct === 'number'
        ? {
            customXPct: clampWatermarkPct(w.customXPct),
            customYPct: clampWatermarkPct(w.customYPct),
          }
        : {}),
    };
    // Rebuild through the factory rather than trusting the stored object.
    // This is the ONE boundary where a watermark enters the session without
    // passing ADD_WATERMARK / UPDATE_WATERMARK, both of which clamp — so an
    // asset carrying opacity 1 (hand-edited, written by another client, or
    // by a future build) used to land in the editor fully opaque and cover
    // the text underneath, which AC#11 forbids. The factory also fills in
    // fields added after the asset was written, so an old document doesn't
    // arrive with `grayscale: undefined` driving a control.
    if (w.type === 'image') {
      const legacy = w as AppliedWatermark & {
        /** Pre-amendment field name: the forced-greyscale base. */
        imageGrayscaleDataUrl?: string | null;
      };
      // The asset is shared storage the public API can write, and both of
      // these end up in an `<img src>` and in a CSS `url()`. The stored form
      // is always a PNG this app produced, so anything else is not ours;
      // dropping it degrades the watermark rather than fetching a stranger's
      // URL from every reviewer's editor.
      const inline = (u: string | null | undefined) =>
        sanitizeStoredImageDataUrl(u);
      return createImageWatermark(w.instanceId, {
        ...w,
        ...anchored,
        rotation: clampWatermarkRotation(w.rotation),
        // Format 1.3.0 stores the raster as its own asset and nothing inline,
        // so both of these are normally null here and the base arrives later
        // via `hydrateWatermarkImageAssets`. A 1.2.0 body still carries its
        // base inline and reading it costs one line — dropping the read would
        // turn every document saved before this change into a mark with no
        // artwork, silently and permanently.
        imageDataUrl: inline(w.imageDataUrl),
        imageBaseDataUrl:
          inline(w.imageBaseDataUrl) ??
          inline(legacy.imageGrayscaleDataUrl) ??
          inline(w.imageDataUrl),
        // App-written and opaque, but this body is writable through the public
        // API by any org member, so anything that is not a non-empty string is
        // dropped rather than handed to a GraphQL query.
        imageAssetId:
          typeof w.imageAssetId === 'string' && w.imageAssetId.trim()
            ? w.imageAssetId
            : null,
      });
    }
    // Length is bounded HERE, not only by the drawer input's `maxLength`,
    // which is a UI hint the public API never sees. An asset-written 1 MB
    // `text` is escaped correctly but is then written into EVERY page's
    // content stream at burn-in and drawn as one nowrap line in the overlay.
    const text = clampWatermarkText(w.text ?? '', MAX_WATERMARK_TEXT_LENGTH);
    return createTextWatermark(w.instanceId, {
      ...w,
      ...anchored,
      text,
      // Resolved HERE, not left to the factory's default.
      //
      // A text mark written before VE-24693 review round 2 carries a `scale`
      // and no `fontScale`. `...w` would then omit the key entirely and the
      // factory would fill in its default — so the mark would come back at
      // the default size regardless of what it was saved at, and the legacy
      // branch of `fontScaleOf` would never run. Reading it once, on the way
      // in, is the migration; every writer downstream stores a coefficient.
      //
      // The conversion is exact rather than approximate — the page width
      // cancels out of it — so an old mark renders at precisely the size it
      // always did, on every page size. See `fontScaleOf`.
      fontScale: fontScaleOf({ ...w, text }),
      // Wrapped for the same reason, and it was missed the first time.
      //
      // The rotation slider narrowed from the full circle to -90..90, because
      // a mark at 135 degrees is a mark at -45 upside down. Only definitions
      // went through the wrap; marks restored from an asset did not. So a
      // document saved under the old control reopened with `rotation: 135`,
      // the slider pinned its thumb at 90 while the mark still rendered at
      // 135, and the operator's first touch of that control snapped the mark
      // 45 degrees — on a document they had opened to review, not to edit.
      rotation: clampWatermarkRotation(w.rotation),
    });
  });
  // The drop, applied last so it reads the SANITISED values — see the header.
  return valid
    .map((src, i) => ({
      src: src as unknown as Record<string, unknown>,
      mark: marks[i]!,
    }))
    .filter(({ src, mark }) => {
      if (mark.type !== 'image' || mark.imageBaseDataUrl || mark.imageAssetId) {
        return true;
      }
      // Nothing usable. Whether that is worth a row depends on WHY, and the raw
      // entry is the only place that answer survives.
      //
      // Something was there and this client REFUSED it — a non-PNG, an
      // oversized value, an `https://` source pointing at another server. The
      // mark stays, without the value: the document does say a watermark
      // belongs here, the operator is the one who can act on that, and the
      // panel flags the row. Dropping it would hide a rejection worth seeing
      // precisely because somebody wrote it deliberately.
      //
      // Nothing was ever there, and the row is what auto-apply wrote before it
      // learned to resolve artwork from the definition's TDO. Nothing to
      // refuse, nothing to recover, and nothing to tell the operator except
      // that a watermark they will never see is listed as applied.
      return ARTWORK_KEYS.some((k) => {
        const v = src[k];
        return typeof v === 'string' && v.trim() !== '';
      });
    })
    .map(({ mark }) => mark);
}

// ---------------------------------------------------------------------------
// Lexicon detections on the asset (VE-24948 / U5)
// ---------------------------------------------------------------------------

/**
 * Splits lexicon detections into the asset's two homes: one card record per
 * (lexicon, entry) at the root, and its matches bucketed by page.
 *
 * A card with no surviving rects contributes nothing — an empty card would
 * reload as a detection the reviewer cannot see, act on, or burn in, while
 * still counting toward the panel's totals.
 */
function buildLexiconAssetRecords(detections: readonly Detection[]): {
  detections: PersistedLexiconDetection[];
  matchesByPage: Record<string, PersistedLexiconMatch[]>;
} {
  const cards: PersistedLexiconDetection[] = [];
  const matchesByPage: Record<string, PersistedLexiconMatch[]> = {};

  for (const d of detections) {
    if (!isLexiconDetection(d)) {
      continue;
    }
    const redactedSet = new Set(d.redactedMatchIds ?? []);
    const deletedSet = new Set(d.deletedMatchIds ?? []);
    const autoRedactedSet = new Set(d.autoRedactedMatchIds ?? []);
    const redactedPages = new Set(redactedPagesOf(d));
    const perMatch = hasMatchIds(d);
    let wrote = false;

    for (const occ of d.occurrences ?? []) {
      const ids = occ.matchIds ?? (occ.id ? [occ.id] : []);
      // `rects` and `matchIds` are index-aligned with ONE ID PER RECT (the
      // contract `DetectionMaskOverlay` reads with `occ.matchIds?.[idx]`), so a
      // match that wraps across rows repeats its id on consecutive rects.
      // Group those back together so one match writes one asset entry carrying
      // all of its row outlines.
      const rowsByMatch = new Map<string, BoundingBox[]>();
      const matchOrder: string[] = [];
      for (let i = 0; i < occ.rects.length; i += 1) {
        const id = ids[i];
        const rect = occ.rects[i];
        if (!id || !rect) {
          continue;
        }
        let rows = rowsByMatch.get(id);
        if (!rows) {
          rows = [];
          rowsByMatch.set(id, rows);
          matchOrder.push(id);
        }
        rows.push(rect);
      }

      for (const id of matchOrder) {
        const rows = rowsByMatch.get(id) ?? [];
        const deleted = deletedSet.has(id);
        const entry: PersistedLexiconMatch = {
          id,
          detectionId: d.id,
          rects: rows.map(boxToPoints),
          redacted: deleted
            ? false
            : perMatch
              ? redactedSet.has(id)
              : redactedPages.has(occ.page),
        };
        if (deleted) {
          entry.deleted = true;
        }
        // Written whenever the match was FIRST redacted automatically, even if
        // a reviewer has since un-redacted it: the question the flag answers is
        // whether anybody reviewed it before it was burned in, and the answer
        // does not change retrospectively.
        if (autoRedactedSet.has(id)) {
          entry.autoRedacted = true;
        }
        (matchesByPage[String(occ.page)] ??= []).push(entry);
        wrote = true;
      }
    }

    if (!wrote) {
      continue;
    }
    const card: PersistedLexiconDetection = {
      id: d.id,
      label: d.label,
      snapshot: d.lexicon,
    };
    if (d.addedToRedactions === true) {
      card.addedToRedactions = true;
    }
    if (d.redactionCode) {
      card.redactionCode = d.redactionCode;
    }
    cards.push(card);
  }

  return { detections: cards, matchesByPage };
}

/** Drops an unset `evaluatedAt` rather than writing `null` into the asset. */
function normalizeAppliedLexicon(
  record: AppliedLexiconRecord
): AppliedLexiconRecord {
  const { evaluatedAt, ...rest } = record;
  return evaluatedAt ? { ...rest, evaluatedAt } : rest;
}

/**
 * Rebuilds lexicon detections from the asset body.
 *
 * Reconstruction consults NO lexicon: everything comes from the stored snapshot
 * and geometry. That is what makes `U5-FR2` hold — a lexicon edited or deleted
 * after application cannot reach back into a saved document, because nothing on
 * this path dereferences one.
 *
 * Malformed records are dropped rather than thrown on, matching
 * `redactionsFromAssetBody`: a partial or future-version asset must still yield
 * a usable editor.
 */
export function lexiconDetectionsFromAssetBody(
  asset: DocumentRedactionAssetV1
): LexiconSourcedDetection[] {
  const cards = Array.isArray(asset.lexiconDetections)
    ? asset.lexiconDetections
    : [];
  if (cards.length === 0) {
    return [];
  }

  /** Matches grouped by card, then page, in page order. */
  const byCard = new Map<
    string,
    Map<number, { rects: BoundingBox[]; matchIds: string[] }>
  >();
  const redacted = new Map<string, Set<string>>();
  const deleted = new Map<string, Set<string>>();
  const autoRedacted = new Map<string, Set<string>>();

  for (const [pageStr, rawPageBucket] of Object.entries(
    asset.redactions ?? {}
  )) {
    const page = Number(pageStr);
    if (!Number.isFinite(page) || page <= 0 || !rawPageBucket) {
      continue;
    }
    const entries = (rawPageBucket as { LEXICON?: PersistedLexiconMatch[] })
      .LEXICON;
    if (!Array.isArray(entries)) {
      continue;
    }
    for (const m of entries) {
      if (
        !m ||
        typeof m.id !== 'string' ||
        typeof m.detectionId !== 'string' ||
        !Array.isArray(m.rects)
      ) {
        continue;
      }
      const boxes = m.rects.filter(isValidPoints).map(pointsToBox);
      if (boxes.length === 0) {
        continue;
      }
      interface PageBucket {
        rects: BoundingBox[];
        matchIds: string[];
      }
      const pages: Map<number, PageBucket> =
        byCard.get(m.detectionId) ?? new Map<number, PageBucket>();
      byCard.set(m.detectionId, pages);
      const bucket: PageBucket = pages.get(page) ?? {
        rects: [],
        matchIds: [],
      };
      // One match id per row rect, index-aligned, so the write path's pairing
      // survives the round trip.
      for (const box of boxes) {
        bucket.rects.push(box);
        bucket.matchIds.push(m.id);
      }
      pages.set(page, bucket);
      if (m.deleted === true) {
        const set = deleted.get(m.detectionId) ?? new Set<string>();
        set.add(m.id);
        deleted.set(m.detectionId, set);
      } else if (m.redacted === true) {
        const set = redacted.get(m.detectionId) ?? new Set<string>();
        set.add(m.id);
        redacted.set(m.detectionId, set);
      }
      // Independent of `redacted` and of `deleted`: it says how the match
      // first became redacted, so it survives a reviewer un-redacting or
      // dismissing it afterwards.
      if (m.autoRedacted === true) {
        const set = autoRedacted.get(m.detectionId) ?? new Set<string>();
        set.add(m.id);
        autoRedacted.set(m.detectionId, set);
      }
    }
  }

  const out: LexiconSourcedDetection[] = [];
  for (const card of cards) {
    if (
      !card ||
      typeof card.id !== 'string' ||
      typeof card.label !== 'string' ||
      !card.snapshot
    ) {
      continue;
    }
    const pages = byCard.get(card.id);
    if (!pages || pages.size === 0) {
      // A card with no matches on any page: nothing to render or burn in.
      continue;
    }
    const occurrences: PiiOccurrence[] = [...pages.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([page, bucket]) => ({
        page,
        rects: bucket.rects,
        id: bucket.matchIds[0] ?? `${card.id}:${page}`,
        matchIds: bucket.matchIds,
      }));

    const base: LexiconSourcedDetection = {
      source: 'lexicon',
      id: card.id,
      label: card.label,
      lexicon: card.snapshot,
      occurrences,
      checked: false,
      expanded: false,
      deletedMatchIds: [...(deleted.get(card.id) ?? [])],
    };
    if (base.deletedMatchIds && base.deletedMatchIds.length === 0) {
      delete base.deletedMatchIds;
    }
    // Derive card state from the per-match flags rather than storing it twice
    // (the same rule `applyPiiMatches` follows).
    const state = deriveDetectionRedaction(base, redacted.get(card.id) ?? []);
    out.push({
      ...base,
      redactedMatchIds: state.redactedMatchIds,
      checkedPages: state.checkedPages,
      checked: state.checked,
      ...(card.addedToRedactions === true ? { addedToRedactions: true } : {}),
      ...(card.redactionCode ? { redactionCode: card.redactionCode } : {}),
      // Restored independently of `redactedMatchIds`, which is derived above:
      // a match auto-redacted on first open and un-redacted by a reviewer
      // since is absent from one list and still present in this one, which is
      // exactly what makes the pair worth persisting.
      ...(autoRedacted.get(card.id)?.size
        ? { autoRedactedMatchIds: [...(autoRedacted.get(card.id) ?? [])] }
        : {}),
    });
  }

  return out;
}

/**
 * How many per-page overlay rects a session would draw, across EVERY overlay.
 *
 * The render window budgets this (`MAX_RESIDENT_RECTS`), and it has to be one
 * number covering both `DetectionMaskOverlay` and `RedactionOverlay`: a budget
 * that counts one and not the other protects against half the problem, which is
 * exactly what the first version of it did (VE-30193).
 *
 * Shared rather than recomputed per viewer because there are three call sites —
 * the editor canvas and, through `useRenderWindow`, Side-by-Side and the
 * redacted-file tab — and they must agree. A viewer that mounts no overlays
 * passes an empty session and correctly gets 0.
 *
 * Counts rects that EXIST, not rects that MOUNT: the overlays gate on the
 * active tab and preview mode, and a budget that tracked those would resize the
 * window on every tab click. Deliberately worst-case.
 */
export function countOverlayRects(session: {
  readonly detections: readonly Detection[];
  readonly redactions: readonly Redaction[];
}): number {
  let rects = 0;
  for (const det of session.detections) {
    for (const occ of det.occurrences) {
      rects += occ.rects.length;
    }
  }
  // One positioned element per redaction, whatever its origin — a custom
  // polygon still draws as a single box.
  return rects + session.redactions.length;
}

/**
 * Applied-lexicon records from the asset body, malformed entries dropped and
 * the pre-VE-29686 page list migrated forward.
 *
 * THE MIGRATION HAPPENS HERE, at the asset boundary, for the reason
 * `pagesFlaggedInAssetBody` below handles its own pre-1.1.0 field name here:
 * a legacy shape that is translated once on read cannot leak into the rest of
 * the app, and the deprecated field drains out of a document the first time it
 * is opened and autosaved. Deferring it to every read site instead leaves
 * `unsearchedPages` alive in live assets for ever, which makes the
 * `@deprecated` note's removal condition unreachable by construction.
 *
 * `unsearchedRangesOf` keeps its own fallback regardless — a record can reach
 * it from a path that never touched an asset, and on this product a redundant
 * read of a disclosure field is the right redundancy to keep.
 */
export function appliedLexiconsFromAssetBody(
  asset: DocumentRedactionAssetV1
): AppliedLexiconRecord[] {
  const records = Array.isArray(asset.appliedLexicons)
    ? asset.appliedLexicons
    : [];
  return records
    .filter(
      (r): r is AppliedLexiconRecord =>
        !!r &&
        typeof r.lexiconId === 'string' &&
        typeof r.lexiconName === 'string' &&
        typeof r.revision === 'number'
    )
    .map((r) => {
      if (!r.incomplete) {
        return r;
      }
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      const { unsearchedPages: legacy, ...rest } = r.incomplete;
      // The three COUNTS are non-optional in the type and rendered straight
      // into the row tooltip, and nothing above checks them. A foreign or
      // truncated `incomplete` missing one printed "Entries that could not be
      // evaluated: undefined." in the one durable disclosure surface.
      const counted = {
        ...rest,
        timedOut: rest.timedOut === true,
        abandonedEntryCount: Number.isFinite(rest.abandonedEntryCount)
          ? rest.abandonedEntryCount
          : 0,
        unresolvedGeometryCount: Number.isFinite(rest.unresolvedGeometryCount)
          ? rest.unresolvedGeometryCount
          : 0,
      };
      // Untouched when the record names no pages either way. The ABSENCE of
      // both fields is itself a signal — it is how `resolveUnloadedAsEvaluated`
      // tells a pre-VE-29686 mid-index mark (which never listed its pages)
      // from one this code wrote — so a normalisation that invented an empty
      // key here would erase it.
      if (legacy === undefined && rest.unsearchedPageRanges === undefined) {
        return { ...r, incomplete: counted };
      }
      // Run through the validating reader UNCONDITIONALLY, not only when the
      // legacy key is present: it migrates and validates in one pass, and a
      // malformed span under the new key is exactly what this boundary exists
      // to stop, because the render path destructures these.
      //
      // An empty result still writes the key when the record had one: the
      // presence of `unsearchedPageRanges: []` is the mid-index signal above.
      //
      // AND IT FAILS CLOSED. This result is written back — `appliedLexicons`
      // is persisted whole on the next autosave — so narrowing the list here
      // narrows it for ever. A dropped span means pages were missed that can
      // no longer be named, which is precisely `pageCountUnknown`; without it,
      // a mark whose ONLY cause was an unreadable list comes back empty, reads
      // as causeless, and autosaves clean over pages nobody searched.
      const { ranges, dropped } = unsearchedCoverageOf(r.incomplete);
      return {
        ...r,
        incomplete: {
          ...counted,
          unsearchedPageRanges: ranges,
          ...(dropped ? { pageCountUnknown: true } : {}),
        },
      };
    });
}

/**
 * Collects the 1-indexed pages whose bucket carries either the current flag or
 * its pre-1.1.0 name. The two names are checked independently rather than
 * preferring one: a legacy asset only has the old name, a 1.1.0 asset has
 * both, and neither name is ever reused for the other feature — so an OR can't
 * mis-attribute a page.
 */
function pagesFlaggedInAssetBody(
  asset: DocumentRedactionAssetV1,
  currentFlag: 'pageContentRemoved' | 'pageHidden',
  legacyFlag: 'pageFullyRedacted' | 'pageRemoved'
): number[] {
  const out: number[] = [];
  for (const [pageStr, rawPageBucket] of Object.entries(
    asset.redactions ?? {}
  )) {
    const page = Number(pageStr);
    // Page keys are written as `String(page)` of a 1-based integer, so only
    // that form is a page. `Number` also accepts "2.5", "1e0" and "0x2", and a
    // fractional page reached the burn-in worker as an index mupdf truncates.
    if (
      !Number.isInteger(page) ||
      page < 1 ||
      String(page) !== pageStr ||
      !rawPageBucket
    ) {
      continue;
    }
    const bucket = rawPageBucket as Record<string, unknown>;
    if (bucket[currentFlag] === true || bucket[legacyFlag] === true) {
      out.push(page);
    }
  }
  return out.sort((a, b) => a - b);
}

/**
 * Returns the 1-indexed pages the user marked "Delete Page" — dropped from the
 * burned-in output entirely. Used by document open to seed
 * `session.excludedPages`. Reads the 1.1.0 `pageHidden` flag and falls back to
 * the pre-1.1.0 `pageRemoved` (VE-20838), which carried the same meaning under
 * the old "Remove Page" label.
 */
export function excludedPagesFromAssetBody(
  asset: DocumentRedactionAssetV1
): number[] {
  return pagesFlaggedInAssetBody(asset, 'pageHidden', 'pageRemoved');
}

/**
 * Returns the 1-indexed pages the user marked "Remove Page" — kept in the
 * output but blanked with the slip-sheet notice. Used by document open to seed
 * `session.pageRedacts`. Reads the 1.1.0 `pageContentRemoved` flag and falls
 * back to the pre-1.1.0 `pageFullyRedacted` (VE-20838), which carried the same
 * meaning under the old "Redact Page" label.
 */
export function pageRedactsFromAssetBody(
  asset: DocumentRedactionAssetV1
): number[] {
  return pagesFlaggedInAssetBody(
    asset,
    'pageContentRemoved',
    'pageFullyRedacted'
  );
}

/**
 * Flatten the per-page `PII` arrays from a persisted asset into a single
 * `PersistedPiiMatch[]` (VE-21915 follow-up). The page-bucket key is the
 * source of truth for the page, but each record already carries its own
 * `page`, so callers can use the records directly. Skips malformed entries.
 */
export function piiMatchesFromAssetBody(
  asset: DocumentRedactionAssetV1
): PersistedPiiMatch[] {
  const out: PersistedPiiMatch[] = [];
  for (const rawPageBucket of Object.values(asset.redactions ?? {})) {
    const pii = (rawPageBucket as { PII?: PersistedPiiMatch[] })?.PII;
    if (!Array.isArray(pii)) {
      continue;
    }
    for (const m of pii) {
      if (m && typeof m.id === 'string' && typeof m.groupId === 'string') {
        out.push(m);
      }
    }
  }
  return out;
}

/**
 * Reverse of `redactionsToAssetBody`. Drops entries whose schema is
 * unrecognized rather than throwing, so a partial / future-version asset
 * still yields a usable redaction list. Coordinates round-trip via the
 * tuple-poly form, which is lossless for axis-aligned rectangles.
 */
export function redactionsFromAssetBody(
  asset: DocumentRedactionAssetV1
): Redaction[] {
  const out: Redaction[] = [];
  for (const [pageStr, rawPageBucket] of Object.entries(
    asset.redactions ?? {}
  )) {
    // VE-20838: the literal page key "all" means the redactions inside
    // were stuck to every page. We restore them with
    // appliesToAllPages=true and a default page of 1 (the field is
    // effectively unused for these — rendering filters by the flag).
    const isAllPages = pageStr === 'all';
    const page = isAllPages ? 1 : Number(pageStr);
    if (!isAllPages && (!Number.isFinite(page) || !rawPageBucket)) {
      continue;
    }
    if (!rawPageBucket) {
      continue;
    }
    const pageBucket = rawPageBucket as Partial<
      Record<PersistedOrigin, Record<string, PersistedRedactionEntry>>
    >;
    for (const origin of PERSISTED_ORIGINS) {
      const originBucket = pageBucket[origin];
      if (!originBucket) {
        continue;
      }
      for (const [id, entry] of Object.entries(originBucket)) {
        if (!entry || !isValidPoints(entry.points)) {
          // Skip silently rather than throw — a partially corrupt asset
          // shouldn't break document open. The user gets a load that's
          // missing the bad entries instead of a load that fails entirely.
          continue;
        }
        // Geometry is the bounding box of the persisted outline (`points`);
        // it's never stored separately.
        const redaction: Redaction = {
          id,
          origin,
          page,
          geometry: pointsToBox(entry.points),
        };
        // A custom polygon keeps its vertices in memory (drives PolygonLayer
        // + per-point editing); a rectangle needs only the derived box.
        if (entry.isCustomPolygon && entry.points.length >= 3) {
          redaction.isCustomPolygon = true;
          redaction.points = entry.points.map(([x, y]) => ({ x, y }));
        }
        if (entry.redactionCode) {
          redaction.redactionCode = entry.redactionCode;
        }
        if (isAllPages) {
          redaction.appliesToAllPages = true;
        }
        // VE-20839: only restore `redacted` when explicitly persisted as
        // false. Old assets (no field) and entries that happen to
        // round-trip `true` both land as `undefined`, which the render
        // gates treat as redacted — keeping the default behavior
        // unchanged.
        if (entry.redacted === false) {
          redaction.redacted = false;
        }
        // VE-20839: restore `phrase` if the writer chose to persist it
        // (keyword + text-select origins always; the older <10-word
        // cap on text-select was dropped — geometry pins the redacted
        // region down regardless of length). Replaces the DOM-walking
        // rehydration pass we used to run on open, which was fragile
        // around font metrics, multi-text-node spans, and adjacent-
        // line bleed.
        if (typeof entry.phrase === 'string' && entry.phrase.trim()) {
          redaction.phrase = entry.phrase;
        }
        if (typeof entry.name === 'string' && entry.name.trim()) {
          redaction.name = entry.name;
        }
        // Restore the text-selection group id so clicking one row reselects
        // the whole selection after reload.
        if (
          typeof entry.textSelectionId === 'string' &&
          entry.textSelectionId
        ) {
          redaction.textSelectionId = entry.textSelectionId;
        }
        // Restore the persisted per-row word count (asset format 1.0.0).
        // Absent on 0.0.3 entries — metrics fall back to counting `phrase`.
        if (typeof entry.wordCount === 'number' && entry.wordCount >= 0) {
          redaction.wordCount = entry.wordCount;
        }
        out.push(redaction);
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Undo / Redo (snapshot-based)
// ---------------------------------------------------------------------------

export function snapshotFromSession(session: SessionState): UndoSnapshot {
  return {
    // Captured BEFORE the mutation, like everything else here — which is what
    // makes a bulk action's selection recoverable: the reducer snapshots, then
    // mutates, and the bar's clear arrives afterwards as its own non-undoable
    // dispatch. See the field's note on UndoSnapshot.
    selectedRedactionIds: session.selectedRedactionIds,
    selectedDetectionRowKeys: session.selectedDetectionRowKeys,
    redactions: session.redactions,
    detections: session.detections,
    detectionRectOverrides: session.detectionRectOverrides,
    pageRedacts: session.pageRedacts,
    excludedPages: session.excludedPages,
    // Must travel with `detections` — see the note on `UndoSnapshot`.
    appliedLexicons: session.appliedLexicons,
    // Ditto — so undoing an apply also stops its BR-48 re-evaluation. The
    // apply-success snapshot overrides this with the pre-request set.
    sessionAppliedLexiconIds: session.lexiconApply.sessionAppliedLexiconIds,
    // Deliberately absent: only `LEXICON_APPLY_SUCCESS` stamps this, on its
    // own snapshot, via the `pushSnapshot` override. See the field's note.
    watermarks: session.watermarks,
    watermarksOff: session.watermarksOff,
    batesStamp: session.batesStamp,
    batesStampOff: session.batesStampOff,
  };
}

/**
 * Where each image mark's raster asset currently lives, keyed by instance.
 *
 * `imageAssetId` is not user-visible state and `SET_WATERMARK_IMAGE_ASSET`
 * deliberately pushes no undo snapshot — but that alone did NOT make it
 * undo-immune, because {@link snapshotFromSession} captures the whole
 * `watermarks` array and undo assigns it back wholesale. So the ordinary
 * sequence "add a watermark, drag it, undo the drag" restored a snapshot taken
 * before the upload finished and set the id back to null, with nothing left to
 * re-run the upload: the saga listens only on ADD_WATERMARK. The mark then
 * saved with no artwork reference at all, and its asset was orphaned.
 *
 * Captured before an undo/redo and reapplied after, so the id survives a
 * traversal of state that never legitimately owned it.
 */
export function watermarkImageAssetIds(
  session: SessionState
): ReadonlyMap<string, string> {
  const ids = new Map<string, string>();
  for (const wm of session.watermarks) {
    if (wm.imageAssetId) {
      ids.set(wm.instanceId, wm.imageAssetId);
    }
  }
  return ids;
}

/**
 * Put the ids from {@link watermarkImageAssetIds} back after an undo/redo.
 *
 * Only fills a BLANK id. A snapshot that already carries one is newer or equal
 * — reasserting the captured value over it would undo a legitimate re-upload.
 * A mark absent from the map keeps whatever the snapshot held.
 */
export function restoreWatermarkImageAssetIds(
  session: SessionState,
  ids: ReadonlyMap<string, string>
): void {
  if (ids.size === 0) {
    return;
  }
  for (const wm of session.watermarks) {
    if (!wm.imageAssetId) {
      const id = ids.get(wm.instanceId);
      if (id) {
        wm.imageAssetId = id;
      }
    }
  }
}

/**
 * Push a snapshot to the undo stack and clear the redo stack.
 * Bounded by UNDO_STACK_LIMIT.
 */
export function pushUndoSnapshot(
  stack: UndoState,
  snapshot: UndoSnapshot
): UndoState {
  const past = [...stack.past, snapshot];
  const trimmed =
    past.length > UNDO_STACK_LIMIT
      ? past.slice(past.length - UNDO_STACK_LIMIT)
      : past;
  return { past: trimmed, future: [] };
}

/**
 * Pop the most recent snapshot off the undo stack and onto the redo stack.
 *
 * Returns the popped snapshot AND the new stack state. The caller is
 * responsible for capturing the *current* session as the redo entry before
 * applying the popped snapshot.
 */
export function popToUndo(
  stack: UndoState,
  currentSnapshot: UndoSnapshot
): { stack: UndoState; popped: UndoSnapshot | null } {
  if (stack.past.length === 0) {
    return { stack, popped: null };
  }
  const past = stack.past.slice(0, stack.past.length - 1);
  const popped = stack.past[stack.past.length - 1]!;
  const future = [...stack.future, currentSnapshot];
  return { stack: { past, future }, popped };
}

export function popToRedo(
  stack: UndoState,
  currentSnapshot: UndoSnapshot
): { stack: UndoState; popped: UndoSnapshot | null } {
  if (stack.future.length === 0) {
    return { stack, popped: null };
  }
  const future = stack.future.slice(0, stack.future.length - 1);
  const popped = stack.future[stack.future.length - 1]!;
  const past = [...stack.past, currentSnapshot];
  return { stack: { past, future }, popped };
}

// ---------------------------------------------------------------------------
// Keyword Search
// ---------------------------------------------------------------------------

/**
 * Zero-width "seam" marker inserted between two text items that visually touch
 * (same word, no gap) instead of the space `join(' ')` would use. A private-use
 * codepoint so it can't collide with real text (and `buildPageText` strips any
 * that a font somehow emits). `findKeywordMatches` treats it as OPTIONAL
 * whitespace — the flat-text analogue of the DOM walker's `spanBreakBefore` — so
 * a word pdf.js split across items (kerning, or a per-glyph OCR layer) is still
 * found, while the one-separator-char-per-item-boundary invariant the keyword
 * saga's offset→rect mapping relies on is preserved (VE-26267).
 */
export const ITEM_SEAM = '';

/** Minimal pdfjs text-item shape `buildPageText` needs (from getTextContent). */
export interface PageTextItem {
  str?: string;
  /** pdfjs affine transform; `[4]` = x, `[5]` = baseline y (PDF user space). */
  transform?: number[];
  width?: number;
  height?: number;
}

// A page is "per-glyph" (OCR) when at least this fraction of its non-whitespace
// items are a single character. Normal prose has only a few ("a", "I", digits,
// punctuation); a per-glyph layer is ~1.0.
const SINGLE_GLYPH_RATIO = 0.6;
// Gap (as a fraction of glyph height ≈ em) below which two items are treated as
// the same word and seamed rather than spaced. Strict on normal pages so only an
// unambiguous mid-word split seams; looser on per-glyph pages whose letter
// spacing is wider and irregular.
const STRICT_GAP_RATIO = 0.1;
const PERGLYPH_GAP_RATIO = 0.25;

/**
 * True when a page's items look like a per-glyph OCR text layer (most items are
 * a single character). Used only to pick how aggressively `buildPageText` seams
 * — it never decides WHETHER to reconstruct, so a misclassification can't cause
 * a missed match, only a slightly different threshold.
 */
export function isPerGlyphTextItems(items: readonly PageTextItem[]): boolean {
  const glyphs = items.filter((it) => (it.str ?? '').trim().length > 0);
  if (glyphs.length < 4) {
    return false;
  }
  const single = glyphs.filter((it) => (it.str ?? '').length === 1).length;
  return single / glyphs.length >= SINGLE_GLYPH_RATIO;
}

/** True when enough glyph items report a usable (>0) width to measure gaps. */
function hasUsableWidths(items: readonly PageTextItem[]): boolean {
  const glyphs = items.filter((it) => (it.str ?? '').trim().length > 0);
  if (glyphs.length === 0) {
    return false;
  }
  const withWidth = glyphs.filter((it) => (it.width ?? 0) > 0).length;
  return withWidth / glyphs.length >= 0.5;
}

/** The separator to place before `cur` given the previous item `prev`. */
function separatorBetween(
  prev: PageTextItem,
  cur: PageTextItem,
  gapRatio: number,
  seamSameLine: boolean
): string {
  const height = prev.height && prev.height > 0 ? prev.height : 12;
  const prevBaseline = prev.transform?.[5] ?? 0;
  const curBaseline = cur.transform?.[5] ?? 0;
  // Different line → a real space (word boundary); the matcher's whitespace-run
  // rule absorbs it whether it reads as a space or a newline.
  if (Math.abs(curBaseline - prevBaseline) > height * 0.5) {
    return ' ';
  }
  // No usable widths on a per-glyph page: gaps can't be measured, so seam every
  // same-line pair rather than degrade to all-spaces (see buildPageText).
  if (seamSameLine) {
    return ITEM_SEAM;
  }
  const prevRight = (prev.transform?.[4] ?? 0) + (prev.width ?? 0);
  const gap = (cur.transform?.[4] ?? 0) - prevRight;
  return gap > height * gapRatio ? ' ' : ITEM_SEAM;
}

/**
 * Build a page's searchable text from its pdfjs items. Replaces the naive
 * `items.map(i => i.str).join(' ')` (which put a space between EVERY item, so a
 * per-glyph OCR page read as "A C C O U N T" and matched nothing): the separator
 * between two items is chosen from geometry — a real space on a gap or line
 * break, else a zero-width `ITEM_SEAM`. Exactly one separator char per boundary,
 * so the keyword saga's offset→item mapping is unchanged (VE-26267).
 */
export function buildPageText(items: readonly PageTextItem[]): string {
  const perGlyph = isPerGlyphTextItems(items);
  const gapRatio = perGlyph ? PERGLYPH_GAP_RATIO : STRICT_GAP_RATIO;
  // A per-glyph page with no usable glyph widths can't have its gaps measured —
  // every boundary would fall back to a space and revert the original miss. Seam
  // all same-line pairs instead: a seam is optional whitespace, so this can only
  // over-match, never miss — the safe direction for a redaction search.
  const seamSameLine = perGlyph && !hasUsableWidths(items);
  let out = '';
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i]!;
    // Strip any real ITEM_SEAM a font emitted, so the sentinel is unambiguous.
    const str = (it.str ?? '').split(ITEM_SEAM).join('');
    if (i === 0) {
      out += str;
      continue;
    }
    out += separatorBetween(items[i - 1]!, it, gapRatio, seamSameLine) + str;
  }
  return out;
}

/** Drop seam sentinels from a slice of page text for display / comparison. */
export function stripItemSeams(text: string): string {
  return text.split(ITEM_SEAM).join('');
}

/** A page's text with seams removed, plus the way back to real offsets. */
export interface SeamlessPageText {
  /** The page text with every `ITEM_SEAM` removed. */
  readonly text: string;
  /**
   * `indexMap[i]` is the offset in the ORIGINAL text of `text[i]`.
   *
   * One extra trailing entry holds the original length, so a match ending at
   * the very end of the page maps without a special case.
   */
  readonly indexMap: readonly number[];
}

/**
 * Strip seams and keep an index map back to the original offsets.
 *
 * This exists for REGEX matching (VE-24948, QA-1). `findKeywordMatches` walks
 * the text char by char and can treat a seam as optional whitespace as it goes;
 * a `RegExp` cannot — it sees `P␀D␀-␀1␀2␀3␀4` and no pattern longer than one
 * character can ever match. Since the MuPDF pre-pass emits one item per GLYPH,
 * every page is seamed at every boundary, so raw regex matching found nothing
 * on any real document.
 *
 * Matching therefore runs on `text` (where the pattern means what its author
 * meant, anchors and `\b` included) and the resulting offsets are translated
 * back through `indexMap`, because every downstream consumer — the geometry
 * resolver, the match id, the persisted record — is in original coordinates.
 *
 * Real whitespace is PRESERVED: only the zero-width seam sentinel is dropped,
 * so a pattern like `\(\d{3}\) \d{3}-\d{4}` still needs its literal space.
 */
export function seamlessPageText(text: string): SeamlessPageText {
  // Fast path: no seams (a normal multi-char-item page, or a test fixture).
  if (!text.includes(ITEM_SEAM)) {
    const indexMap = new Array<number>(text.length + 1);
    for (let i = 0; i <= text.length; i += 1) {
      indexMap[i] = i;
    }
    return { text, indexMap };
  }
  let out = '';
  const indexMap: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i]!;
    if (c === ITEM_SEAM) {
      continue;
    }
    indexMap.push(i);
    out += c;
  }
  indexMap.push(text.length);
  return { text: out, indexMap };
}

/** Where a keyword match starts in the searched text, and how long it ran. */
export interface KeywordMatchSpan {
  index: number;
  /**
   * Chars matched in the ORIGINAL text. Can exceed the phrase's length: one
   * space in the phrase matches a whole whitespace run in the text (see
   * `findKeywordMatches`).
   */
  length: number;
}

/** A phrase as `findKeywordMatches` compares it: trimmed, whitespace runs → one space. */
export function normalizeKeywordPhrase(phrase: string): string {
  return phrase.trim().replace(/\s+/g, ' ');
}

/**
 * `text` as `findKeywordMatches` compares it, seams dropped. Folded BEFORE the
 * seams go, as the matcher folds: context-sensitive folds (final sigma) must
 * see the same neighbours on both sides.
 */
export function keywordHaystack(text: string, matchCase: boolean): string {
  return stripItemSeams(matchCase ? text : text.toLowerCase());
}

/**
 * False only when `findKeywordMatches(text, phrase, { matchCase })` cannot
 * match: its longest space-free token is absent from `haystack`, which must be
 * `keywordHaystack(text, matchCase)`. A native `includes` in place of the
 * matcher's char walk, so skipping absent phrases is cheap (VE-29502).
 */
export function keywordPhraseMayMatch(
  haystack: string,
  phrase: string,
  matchCase: boolean
): boolean {
  const normalized = normalizeKeywordPhrase(phrase);
  const needle = matchCase ? normalized : normalized.toLowerCase();
  const longest = needle
    .split(' ')
    .reduce((a, b) => (b.length > a.length ? b : a), '');
  return haystack.includes(longest);
}

/**
 * Find all occurrences of `phrase` in `text` — leftmost, non-overlapping,
 * case-insensitive substring by default. `matchCase` honors casing (the Aa
 * toggle); `wholeWord` requires non-word chars around each match.
 *
 * ONE SPACE IN THE PHRASE MATCHES A WHOLE WHITESPACE RUN IN THE TEXT. Page text
 * is `items.map(i => i.str).join(' ')` and pdfjs often already ends an item
 * with a space, so a phrase spanning two items sits across a DOUBLE space and
 * an exact scan found nothing — the rail reported 0 matches for a phrase in
 * plain sight, and the panel's "K of N" collapsed to "K of K" (VE-26267).
 *
 * `ITEM_SEAM` sentinels (from `buildPageText` on a per-glyph / split-word page)
 * are treated as OPTIONAL whitespace — a seam satisfies a phrase space OR is
 * skipped for a no-space phrase — so `"John␀Smith"` matches both `John Smith`
 * and `JohnSmith`. This is the flat-text version of the DOM walker's optional
 * seam; a real gap keeps its space, so a no-space phrase still won't cross a
 * genuine word boundary.
 *
 * Indices are offsets into the ORIGINAL `text`, so keywordSearch's offset →
 * item rect lookup stays valid. Property: ascending and within bounds.
 *
 * `keywordPhraseMayMatch` relies on two facts here: a phrase's space-free
 * tokens match contiguously once seams are dropped, and case is folded over the
 * whole text. Change either and change it too (VE-29502).
 */
export function findKeywordMatches(
  text: string,
  phrase: string,
  options: { matchCase?: boolean; wholeWord?: boolean } = {}
): KeywordMatchSpan[] {
  const normalized = normalizeKeywordPhrase(phrase);
  if (!normalized) {
    return [];
  }
  const { matchCase = false, wholeWord = false } = options;
  const haystack = matchCase ? text : text.toLowerCase();
  const needle = matchCase ? normalized : normalized.toLowerCase();
  // `\w` covers ASCII alphanumerics + `_`. Seams are zero-width sentinels, not
  // characters: the whole-word boundary check below looks THROUGH any seam run
  // to the first real neighbor before classifying it, so a seam is never tested
  // here. (treating the seam itself as a word char rejected every
  // whole-word match, since a per-glyph boundary is always `word␀ ␀word`.)
  const isWordChar = (c: string | undefined) => c !== undefined && /\w/.test(c);
  const isSpace = (c: string | undefined) => c !== undefined && /\s/.test(c);
  const isSpaceOrSeam = (c: string | undefined) =>
    c === ITEM_SEAM || isSpace(c);
  const matches: KeywordMatchSpan[] = [];
  let start = 0;
  while (start < haystack.length) {
    // Never anchor a match on a seam — the recorded index must land on a real
    // item char for the saga's offset→rect lookup. The first needle char is
    // always non-space, so the real start is the next glyph.
    if (haystack[start] === ITEM_SEAM) {
      start += 1;
      continue;
    }
    let cursor = start;
    let matched = true;
    for (let k = 0; k < needle.length; k += 1) {
      if (needle[k] === ' ') {
        // A phrase space matches a run of real whitespace and/or seams.
        if (!isSpaceOrSeam(haystack[cursor])) {
          matched = false;
          break;
        }
        while (isSpaceOrSeam(haystack[cursor])) {
          cursor += 1;
        }
        continue;
      }
      // Skip zero-width seams so a word split across items still matches.
      while (haystack[cursor] === ITEM_SEAM) {
        cursor += 1;
      }
      if (haystack[cursor] !== needle[k]) {
        matched = false;
        break;
      }
      cursor += 1;
    }
    if (!matched) {
      start += 1;
      continue;
    }
    if (wholeWord) {
      // Look past ITEM_SEAM sentinels to the first REAL neighbor on each side,
      // mirroring the core scan's seam-skip above. A seam is zero-width optional
      // whitespace (a same-word join OR a seamed space), so the boundary is
      // decided by the real glyph beyond it — not the seam itself. Without this
      // skip a per-glyph boundary `word␀ ␀word` reads its neighbor as the seam,
      // so every whole-word match is (wrongly) rejected.
      let b = start - 1;
      while (b >= 0 && haystack[b] === ITEM_SEAM) {
        b -= 1;
      }
      let a = cursor;
      while (a < haystack.length && haystack[a] === ITEM_SEAM) {
        a += 1;
      }
      const before = b >= 0 ? haystack[b] : undefined;
      const after = a < haystack.length ? haystack[a] : undefined;
      if (isWordChar(before) || isWordChar(after)) {
        start += 1;
        continue;
      }
    }
    matches.push({ index: start, length: cursor - start });
    start = cursor;
  }
  return matches;
}

/** `findKeywordMatches` when only the start offsets are needed. */
export function findKeywordMatchIndices(
  text: string,
  phrase: string,
  options: { matchCase?: boolean; wholeWord?: boolean } = {}
): number[] {
  return findKeywordMatches(text, phrase, options).map((m) => m.index);
}

// ---------------------------------------------------------------------------
// Detection Toggle Logic
// ---------------------------------------------------------------------------

/**
 * Re-derive a parent detection's `checked` from its `checkedPages`.
 */
export function deriveGroupCheckedState(
  occurrences: PiiOccurrence[],
  checkedPages: number[]
): boolean | 'partial' {
  const pagesInDetection = new Set(occurrences.map((o) => o.page));
  const allChecked = [...pagesInDetection].every((p) =>
    checkedPages.includes(p)
  );
  if (allChecked && pagesInDetection.size > 0) {
    return true;
  }
  if (checkedPages.length === 0) {
    return false;
  }
  return 'partial';
}

/**
 * Every vendor match id of a detection, positionally aligned with its rects
 * (one id per match across all occurrences). Empty for legacy detections whose
 * occurrences carry no vendor ids.
 */
export function allMatchIdsOf(d: DetectionBase): string[] {
  const out: string[] = [];
  for (const occ of d.occurrences ?? []) {
    if (occ.matchIds && occ.matchIds.length) {
      out.push(...occ.matchIds);
    } else if (occ.id) {
      out.push(occ.id);
    }
  }
  return out;
}

/**
 * Match ids of a detection excluding ones the user deleted — the "live" set
 * that drives redaction toggles and the card checkbox math.
 */
export function liveMatchIdsOf(d: DetectionBase): string[] {
  const deleted = new Set(d.deletedMatchIds ?? []);
  return allMatchIdsOf(d).filter((id) => !deleted.has(id));
}

/**
 * Whether a detection supports per-match redaction (its occurrences carry
 * vendor match ids). When false, callers fall back to the page-level
 * `checked` / `checkedPages` path.
 */
export function hasMatchIds(d: DetectionBase): boolean {
  return allMatchIdsOf(d).length > 0;
}

/**
 * Whether bulk Redact can still change this detection.
 *
 * On `DetectionBase`, like its siblings above, so it covers lexicon-sourced
 * cards as well as PII ones — the rule is about redaction state, which both
 * share.
 *
 * `'partial'` (some pages / matches redacted, the rest not) IS redactable: the
 * bar dispatches SET_DETECTIONS_REDACTED({redacted: true}), which completes a
 * partial detection on both reducer branches. Excluding it (VE-28716 review
 * finding B1) left partially-redacted PII unredactable in bulk, with the
 * button greyed out and no other way to finish the job — an under-redaction
 * trap in a redaction product. Matches DetectionList's "Redact All" master
 * checkbox, which has used `checked !== true` all along.
 */
export const isBulkRedactable = (d: DetectionBase): boolean =>
  d.checked !== true;

/**
 * Whether bulk Unredact can still change this detection: anything with at
 * least one match redacted, `'partial'` included.
 *
 * `'partial'` used to be EXCLUDED. The bar then drove TOGGLE_DETECTION, which
 * on a partial detection REDACTS the rest — the inverse of this button's label
 * (VE-28716 review finding B2). The bar now dispatches SET_DETECTIONS_REDACTED,
 * which sets the state absolutely, so `redacted: false` clears a partial to
 * 0-of-N on both reducer branches. Excluding it left a match unredacted on the
 * canvas with no bulk way to clear the rest: the bar read "Unredact (2 of 3)"
 * and skipped it (VE-30158). It is one undo step, and undo restores the
 * selection, so the per-match choices it discards come back with one ctrl-Z.
 */
export function isBulkUnredactable(d: DetectionBase): boolean {
  return d.checked === true || d.checked === 'partial';
}

/** Whether bulk Remove can drop this detection from the Redactions list. */
export function isBulkRemovable(d: DetectionBase): boolean {
  return d.addedToRedactions === true;
}

/**
 * Derive a detection's card state from a per-match redacted set. Returns the
 * normalized `redactedMatchIds` (intersected with the detection's own ids),
 * `checkedPages` (any match on the page redacted — kept for legacy/UI
 * consumers and the thumbnail rail), and the tri-state `checked` reflecting
 * whether none / some / all of the group's matches are redacted.
 */
/**
 * What survives when a lexicon is taken off the document (VE-24948 / U5).
 *
 * The rule is REDACTED MATCHES SURVIVE, everything else goes. A match the
 * reviewer redacted is their work, not the lexicon's: it is burned into the
 * export (`buildRedactionPlan` emits from `redactedMatchIds`), it is in the
 * Redactions list, and a disclosure record depends on it. A candidate they
 * never acted on is the lexicon's suggestion and leaves with the lexicon.
 *
 * `redactedMatchIds` is the signal, not `addedToRedactions`. The latter is
 * per-CARD and sticky — set the first time any match is redacted and never
 * cleared by un-redacting — so a card the reviewer promoted and then fully
 * un-redacted still carries it. Keeping such a card would preserve a row with
 * nothing redacted in it, which is a suggestion again, not work.
 *
 * THREE THINGS SHIFT WHEN RECTS ARE DROPPED, and each is a defect if missed:
 *
 *  1. `detectionRectOverrides` is keyed by `detectionId|page|rectIdx`, and
 *     rectIdx is a POSITION in `occ.rects`. Dropping rect 0 renumbers every
 *     rect after it, so an un-remapped override silently moves onto a
 *     different match's box — a reviewer's hand-nudged redaction jumping to
 *     another word. The returned `rectKeyRemap` is how the caller fixes it.
 *  2. `occ.id` is "the first match's id" (see `PiiOccurrence`). If the first
 *     match is dropped, it has to become the first SURVIVING one.
 *  3. `checked` / `checkedPages` are derived from the redacted set against the
 *     live matches. Both change when the live set shrinks, so they are
 *     recomputed rather than carried over.
 *
 * `deletedMatchIds` is carried WHOLE, for the reason a tombstone exists at
 * all: it must name a match that could otherwise come back — and after a
 * remove-and-reapply, every dismissed match can.
 *
 * Returns `null` for a card with nothing redacted — the caller drops it.
 */
export function keepRedactedMatches(detection: Detection): {
  readonly detection: Detection;
  /** Old rect key -> new rect key, for every rect that survived. */
  readonly rectKeyRemap: ReadonlyMap<DetectionRectKey, DetectionRectKey>;
} | null {
  const redacted = new Set(detection.redactedMatchIds ?? []);
  if (redacted.size === 0) {
    return null;
  }

  const rectKeyRemap = new Map<DetectionRectKey, DetectionRectKey>();
  const occurrences: PiiOccurrence[] = [];

  for (const occ of detection.occurrences) {
    const keptRects: BoundingBox[] = [];
    const keptMatchIds: string[] = [];
    occ.rects.forEach((rect, rectIdx) => {
      // No match id means the legacy page-level path, where a rect cannot be
      // attributed to a match. Nothing there can be shown to be redacted, so
      // nothing there survives.
      const matchId = occ.matchIds?.[rectIdx];
      if (!matchId || !redacted.has(matchId)) {
        return;
      }
      rectKeyRemap.set(
        detectionRectKey(detection.id, occ.page, rectIdx),
        detectionRectKey(detection.id, occ.page, keptRects.length)
      );
      keptRects.push(rect);
      keptMatchIds.push(matchId);
    });
    if (keptRects.length === 0) {
      continue;
    }
    occurrences.push({
      ...occ,
      rects: keptRects,
      matchIds: keptMatchIds,
      // See (2): `id` is the first match's, and the first match may be gone.
      ...(occ.id ? { id: keptMatchIds[0] } : {}),
    });
  }

  if (occurrences.length === 0) {
    return null;
  }

  const survivors = new Set(keptMatchIdsOf(occurrences));
  const pruned: Detection = {
    ...detection,
    occurrences,
    // TOMBSTONES ARE CARRIED WHOLE, not filtered against the survivors.
    //
    // The filter that used to be here was provably always empty: `survivors`
    // is built from occurrences already narrowed to REDACTED matches, and
    // `DELETE_DETECTION_MATCH` drops an id from `redactedMatchIds` as it adds
    // it to `deletedMatchIds` — so the two sets are disjoint by construction
    // and the intersection could never be anything else.
    //
    // The effect was to wipe every tombstone on removal, so a reviewer who
    // dismissed a match, removed the lexicon and re-applied it got that match
    // back as a live candidate: `mergeLexiconMatches`'s M3 check found no
    // tombstone to honour. US-U5-6 says applying "does not reset my
    // decisions", and a dismissal is a decision.
    //
    // Keeping them is also the safe direction. A tombstone for a match that
    // never returns suppresses nothing; a missing one resurrects a decision
    // the reviewer already made, which in a redaction tool means re-showing
    // something they judged not to need redacting — and silently.
    ...(detection.deletedMatchIds
      ? { deletedMatchIds: [...detection.deletedMatchIds] }
      : {}),
  };
  const derived = deriveDetectionRedaction(
    pruned,
    [...redacted].filter((id) => survivors.has(id))
  );
  return {
    detection: {
      ...pruned,
      redactedMatchIds: derived.redactedMatchIds,
      checkedPages: derived.checkedPages,
      checked: derived.checked,
    },
    rectKeyRemap,
  };
}

/** Every match id across a set of occurrences, in order. */
function keptMatchIdsOf(occurrences: readonly PiiOccurrence[]): string[] {
  return occurrences.flatMap((occ) => occ.matchIds ?? (occ.id ? [occ.id] : []));
}

export function deriveDetectionRedaction(
  d: DetectionBase,
  redactedMatchIds: Iterable<string>
): {
  redactedMatchIds: string[];
  checkedPages: number[];
  checked: boolean | 'partial';
} {
  // "All" is the live (non-deleted) match set: a fully-redacted card means
  // every remaining match is redacted, ignoring deleted ones.
  const all = liveMatchIdsOf(d);
  const allSet = new Set(all);
  const redactedSet = new Set(
    [...redactedMatchIds].filter((id) => allSet.has(id))
  );
  const checkedPages: number[] = [];
  for (const occ of d.occurrences ?? []) {
    const ids = occ.matchIds ?? (occ.id ? [occ.id] : []);
    if (ids.some((id) => redactedSet.has(id))) {
      checkedPages.push(occ.page);
    }
  }
  let checked: boolean | 'partial';
  if (redactedSet.size === 0) {
    checked = false;
    // Compared against the DEDUPED set, not `all.length`: an occurrence lists
    // one match id per rect, so a match that wraps across rows repeats its id.
    // Comparing against the raw length would report a fully-redacted card as
    // 'partial' forever (VE-24948 / U5).
    //
    // The same comparison keeps this predicate equivalent to the one
    // TOGGLE_DETECTION uses (`all.every(id => cur.has(id))`, which is
    // dedup-insensitive). If they diverged, a card stuck at 'partial' would
    // still be offered to bulk Redact while the reducer considered it fully
    // redacted — and Redact would UNREDACT it (VE-28716).
  } else if (allSet.size > 0 && redactedSet.size === allSet.size) {
    checked = true;
  } else {
    checked = 'partial';
  }
  return { redactedMatchIds: [...redactedSet], checkedPages, checked };
}

/**
 * Set a detection's check state ABSOLUTELY, where `toggleDetection` below
 * flips it. Same two shapes: `setDetectionRedacted(d, true)` equals
 * `toggleDetection(d)` whenever `d` is not already fully checked. `(d, false)`
 * DIVERGES from it on a `'partial'` detection — it clears it, where the toggle
 * would redact the rest — and the bulk bar relies on that, since bulk Unredact
 * offers partials (`isBulkUnredactable`, VE-30158).
 *
 * It exists so a bulk action can be ONE dispatch. Driving N rows through
 * TOGGLE_DETECTION pushed N undo snapshots, so reversing one click took N
 * presses of ctrl-Z and a large selection evicted the rest of the history past
 * UNDO_STACK_LIMIT (VE-28716).
 */
export function setDetectionRedacted<T extends DetectionBase>(
  detection: T,
  redacted: boolean
): T {
  if (detection.occurrences.length <= 1) {
    return { ...detection, checked: redacted };
  }
  // Groups carry the per-page set alongside the flag; the two must agree or
  // the card renders checked with nothing ticked under it.
  return redacted
    ? {
        ...detection,
        checked: true,
        checkedPages: detection.occurrences.map((o) => o.page),
      }
    : { ...detection, checked: false, checkedPages: [] };
}

/**
 * Toggle a detection's check state.
 * - For single-occurrence detections: flip true <-> false.
 * - For groups: toggle to 'all' or 'none' based on current state.
 */
export function toggleDetection<T extends DetectionBase>(detection: T): T {
  if (detection.occurrences.length <= 1) {
    return { ...detection, checked: detection.checked === true ? false : true };
  }
  const allPages = detection.occurrences.map((o) => o.page);
  const checkedPages = detection.checkedPages || [];
  if (checkedPages.length === allPages.length) {
    return { ...detection, checked: false, checkedPages: [] };
  }
  return { ...detection, checked: true, checkedPages: allPages };
}

// ---------------------------------------------------------------------------
// Redaction word/match metrics
// ---------------------------------------------------------------------------

/**
 * Count whitespace-delimited words in a phrase. Empty / whitespace-only input
 * (and undefined) yields 0. Used to total words redacted per category.
 */
export function countWords(phrase?: string): number {
  if (!phrase) {
    return 0;
  }
  const trimmed = phrase.trim();
  if (!trimmed) {
    return 0;
  }
  return trimmed.split(/\s+/).length;
}

/**
 * Number of PII matches currently redacted (burned in) for a detection. Uses
 * the per-match `redactedMatchIds` set when the detection carries vendor ids,
 * else falls back to the legacy page-level `checked` / `checkedPages` path.
 */
function redactedMatchCount(d: DetectionBase): number {
  if (hasMatchIds(d)) {
    return deriveDetectionRedaction(d, d.redactedMatchIds ?? [])
      .redactedMatchIds.length;
  }
  if (d.checked === true) {
    return d.occurrences.length;
  }
  if (d.checked === 'partial') {
    const pages = new Set(d.checkedPages ?? []);
    return d.occurrences.filter((o) => pages.has(o.page)).length;
  }
  return 0;
}

/**
 * Every page a phrase can be navigated to: pages whose extracted text contains
 * it, UNIONED with pages already carrying a redaction for it.
 *
 * Extracted text is an incomplete view — it arrives only once a page renders, a
 * scanned page contributes none, and the DOM walker matches phrases flat text
 * can't. Listing only text hits hid pages the user demonstrably had a redaction
 * on, and this list is how they navigate to their own work (VE-26267).
 */
export function pagesWithPhrase(
  pageTexts: Record<number, string>,
  phrase: string,
  options: { matchCase?: boolean; wholeWord?: boolean } = {},
  redactedPages: readonly number[] = []
): number[] {
  const pages = new Set<number>();
  for (const [pageStr, text] of Object.entries(pageTexts)) {
    if (!text) {
      continue;
    }
    const pageNum = Number(pageStr);
    if (!Number.isFinite(pageNum) || pageNum <= 0) {
      continue;
    }
    if (findKeywordMatches(text, phrase, options).length > 0) {
      pages.add(pageNum);
    }
  }
  for (const page of redactedPages) {
    if (Number.isFinite(page) && page > 0) {
      pages.add(page);
    }
  }
  return Array.from(pages).sort((a, b) => a - b);
}

/**
 * Which OCCURRENCE a redaction row belongs to. A wrapped match/selection is one
 * box per row sharing a `textSelectionId`; a row without one is its own
 * occurrence (so records predating the id count as before). Anything reporting
 * "how many did we redact" must count these, not raw rows — otherwise a wrapped
 * address reads as two against a total that counts it once (VE-26267).
 */
export function redactionOccurrenceKey(redaction: Redaction): string {
  return redaction.textSelectionId ?? redaction.id;
}

/** How many distinct occurrences a set of redaction rows covers. */
export function countRedactionOccurrences(
  redactions: readonly Redaction[]
): number {
  return new Set(redactions.map(redactionOccurrenceKey)).size;
}

/**
 * "K of N redacted" for a grouped phrase card (keyword / lexicon rows in the
 * Redactions list), both sides counted in occurrences.
 *
 * K counts only occurrences whose rows are ALL redacted (`redacted` undefined
 * reads as redacted, per the model). Unredacting a box on the canvas flips
 * `redacted` off but keeps the row, so counting rows regardless of the flag
 * left the card at "26 of 26" after an unredact (VE-30158). A wrapped match
 * with one row unredacted still shows text, so it is not counted.
 *
 * N is the text scan's match count, floored at the occurrences the group holds
 * — redacted or not — because the DOM walker can find matches the flat text
 * scan cannot (see the caller). Flooring at K instead would pull N down with
 * every unredact. Callers scoping to one page pass that page's rows; text
 * matches never span pages, so an occurrence is never counted on two.
 *
 * Deliberately stricter than `computeRedactionMetrics`, which counts an
 * occurrence with ANY redacted row: that reports what the burn-in covered,
 * and a half-redacted wrapped match did burn its active rows. This card asks
 * whether the match is fully hidden.
 */
export function redactionGroupStats(
  rows: readonly Redaction[],
  textMatchCount: number
): { redactedCount: number; totalMatches: number } {
  const unredacted = new Set(
    rows.filter((r) => r.redacted === false).map(redactionOccurrenceKey)
  );
  const all = new Set(rows.map(redactionOccurrenceKey));
  return {
    redactedCount: all.size - unredacted.size,
    totalMatches: Math.max(textMatchCount, all.size),
  };
}

/**
 * Build the per-category redaction metrics persisted on the burned-in asset.
 * Counts only redacted records (`redacted !== false` for redactions; the
 * redacted-match set for PII). `detection.label` is the matched PII text, so
 * PII word count = redacted-match count × words in the phrase.
 *
 * Text-select and keyword counts are per STRETCH OF TEXT, not per stored box: a
 * match or selection that wraps a line is several row boxes sharing one
 * `textSelectionId` and counts once (see `Redaction.textSelectionId`). Rows
 * without that id are their own group, so records predating it total as before.
 * An occurrence counts if ANY of its rows is redacted — it reports what was
 * burned. The Redactions-tab card (`redactionGroupStats`) is stricter on
 * purpose; see there.
 */
export function computeRedactionMetrics(
  session: Pick<SessionState, 'redactions' | 'detections'>
): RedactionMetricsBreakdown {
  const textSelects = session.redactions.filter(
    (r) => r.origin === 'text-select' && r.redacted !== false
  );
  const textSelectSpans = new Set<string>();
  let textSelectWords = 0;
  for (const r of textSelects) {
    if (r.textSelectionId) {
      textSelectSpans.add(r.textSelectionId);
    }
    // Prefer the persisted per-row count; fall back to the phrase so older
    // in-memory rows (pre-wordCount) still total correctly.
    textSelectWords += r.wordCount ?? countWords(r.phrase);
  }
  const textSelect: TextSelectMetrics = {
    rowCount: textSelects.length,
    wordCount: textSelectWords,
    uniqueCount: textSelectSpans.size,
  };

  const keywordRedactions = session.redactions.filter(
    (r) => r.origin === 'keyword' && r.redacted !== false
  );
  const keywordPhrases = new Set<string>();
  // Per occurrence, not per row box — counting rows would report one wrapped
  // address as two matches and double its words.
  const keywordOccurrences = new Map<string, Redaction>();
  for (const r of keywordRedactions) {
    keywordOccurrences.set(redactionOccurrenceKey(r), r);
  }
  let keywordWords = 0;
  for (const r of keywordOccurrences.values()) {
    const phrase = r.phrase?.trim() ?? '';
    if (phrase) {
      keywordPhrases.add(phrase);
    }
    keywordWords += countWords(r.phrase);
  }
  const keywords: KeywordMetrics = {
    uniqueCount: keywordPhrases.size,
    totalCount: keywordOccurrences.size,
    wordCount: keywordWords,
  };

  let piiUnique = 0;
  let piiTotal = 0;
  let piiWords = 0;
  for (const d of session.detections) {
    // Lexicon-sourced redactions are NOT PII-engine output, and counting them
    // here would report agency terms as engine detections in the metrics on the
    // burned-in asset — a report used for disclosure compliance. Under-counting
    // a category that does not exist yet is recoverable; mislabelling is not.
    // A `lexicon` metrics category is open item U5-10 (VE-24948 / U5).
    if (isLexiconDetection(d)) {
      continue;
    }
    const matches = redactedMatchCount(d);
    if (matches === 0) {
      continue;
    }
    piiUnique += 1;
    piiTotal += matches;
    piiWords += matches * countWords(d.label);
  }
  const pii: PiiMetrics = {
    uniqueCount: piiUnique,
    totalCount: piiTotal,
    wordCount: piiWords,
  };

  return {
    textSelect,
    keywords,
    PII: pii,
    totalWordsRedacted:
      textSelect.wordCount + keywords.wordCount + pii.wordCount,
  };
}

// ---------------------------------------------------------------------------
// Asset details parsing (VE-20837)
// ---------------------------------------------------------------------------

/**
 * Defensive read of `details.version` from a redacted-document asset record.
 * The Veritone GraphQL API occasionally returns JSONData as a serialized
 * string rather than an object, depending on the consumer. Handle both,
 * and return 0 for anything we can't parse so callers can use it as a
 * monotonic-max input without further checks.
 */
function parseDetails(details: unknown): Record<string, unknown> | null {
  if (!details) {
    return null;
  }
  let obj: unknown = details;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return null;
    }
  }
  return typeof obj === 'object' && obj !== null
    ? (obj as Record<string, unknown>)
    : null;
}

export function readAssetVersion(details: unknown): number {
  const obj = parseDetails(details);
  if (!obj) {
    return 0;
  }
  const v = obj.version;
  return typeof v === 'number' ? v : 0;
}

/**
 * Page count from `details.numPages`. Used to populate the Redacted Files
 * tab's "Num Pages" row on reopen without re-rendering the PDF.
 */
export function readAssetNumPages(details: unknown): number | null {
  const obj = parseDetails(details);
  if (!obj) {
    return null;
  }
  const v = obj.numPages;
  return typeof v === 'number' && v > 0 ? v : null;
}

function readMetrics(details: unknown): Record<string, unknown> | null {
  const obj = parseDetails(details);
  if (!obj) {
    return null;
  }
  const metrics = obj.metrics;
  return typeof metrics === 'object' && metrics !== null
    ? (metrics as Record<string, unknown>)
    : null;
}

function readOriginCount(
  metrics: Record<string, unknown> | null,
  origin: string
): number {
  if (!metrics) {
    return 0;
  }
  const bucket = metrics[origin];
  if (typeof bucket !== 'object' || bucket === null) {
    return 0;
  }
  const count = (bucket as Record<string, unknown>).count;
  return typeof count === 'number' && count >= 0 ? count : 0;
}

/**
 * Manual-origin redaction count from `details.metrics.manual.count`.
 */
export function readAssetManualRedactionCount(details: unknown): number {
  return readOriginCount(readMetrics(details), 'manual');
}

/** Read a non-negative numeric field from a metrics sub-bucket (else 0). */
function readMetricField(
  metrics: Record<string, unknown> | null,
  bucketKey: string,
  field: string
): number {
  if (!metrics) {
    return 0;
  }
  const bucket = metrics[bucketKey];
  if (typeof bucket !== 'object' || bucket === null) {
    return 0;
  }
  const v = (bucket as Record<string, unknown>)[field];
  return typeof v === 'number' && v >= 0 ? v : 0;
}

/**
 * Text-selection metrics from `details.metrics.textSelect`. Tolerates the
 * legacy `{ count }` shape (maps `count` → `rowCount`, words/spans 0) so older
 * assets still render sensibly; not back-filled.
 */
export function readAssetTextSelectMetrics(
  details: unknown
): TextSelectMetrics {
  const m = readMetrics(details);
  return {
    rowCount:
      readMetricField(m, 'textSelect', 'rowCount') ||
      readMetricField(m, 'textSelect', 'count'),
    wordCount: readMetricField(m, 'textSelect', 'wordCount'),
    uniqueCount: readMetricField(m, 'textSelect', 'uniqueCount'),
  };
}

/** Keyword-match metrics from `details.metrics.keywords` (all 0 if absent). */
export function readAssetKeywordMetrics(details: unknown): KeywordMetrics {
  const m = readMetrics(details);
  return {
    uniqueCount: readMetricField(m, 'keywords', 'uniqueCount'),
    totalCount: readMetricField(m, 'keywords', 'totalCount'),
    wordCount: readMetricField(m, 'keywords', 'wordCount'),
  };
}

/** PII-match metrics from `details.metrics.PII` (all 0 if absent). */
export function readAssetPiiMetrics(details: unknown): PiiMetrics {
  const m = readMetrics(details);
  return {
    uniqueCount: readMetricField(m, 'PII', 'uniqueCount'),
    totalCount: readMetricField(m, 'PII', 'totalCount'),
    wordCount: readMetricField(m, 'PII', 'wordCount'),
  };
}

/**
 * Total words redacted across text-selection, keyword-match, and PII-match —
 * the sum each category's `wordCount`. Convenience for the Redacted File tab's
 * words-redacted metric.
 */
export function totalWordsRedacted(v: {
  textSelect: TextSelectMetrics;
  keywords: KeywordMetrics;
  pii: PiiMetrics;
}): number {
  return v.textSelect.wordCount + v.keywords.wordCount + v.pii.wordCount;
}

/**
 * Cumulative download count from `details.metrics.downloads`. Mirrors the
 * video flow's `metrics.downloads`. Incremented by the saga that handles
 * download clicks; persisted back to the asset.
 */
export function readAssetDownloadCount(details: unknown): number {
  const metrics = readMetrics(details);
  if (!metrics) {
    return 0;
  }
  const v = metrics.downloads;
  return typeof v === 'number' && v >= 0 ? v : 0;
}

/**
 * Count of pages dropped from the burned-in PDF ("Delete Page"), sourced from
 * `details.metrics.pagesRemoved` (preferred) with a top-level
 * `details.pagesRemoved` fallback for older assets written before the
 * metric was nested. Drives the Redacted Files tab's "Pages Deleted" row.
 */
export function readAssetPagesRemovedCount(details: unknown): number {
  const obj = parseDetails(details);
  if (!obj) {
    return 0;
  }
  const metrics = readMetrics(details);
  const fromMetrics = metrics?.pagesRemoved;
  if (typeof fromMetrics === 'number' && fromMetrics >= 0) {
    return fromMetrics;
  }
  const topLevel = obj.pagesRemoved;
  return typeof topLevel === 'number' && topLevel >= 0 ? topLevel : 0;
}

/**
 * Count of pages slip-sheeted in the burned-in PDF ("Remove Page"), sourced
 * from `details.metrics.pagesFullyRedacted` (preferred) with a top-level
 * `details.pagesFullyRedacted` fallback. Drives the Redacted Files tab's
 * "Pages Removed" metric.
 */
export function readAssetPagesFullyRedactedCount(details: unknown): number {
  const obj = parseDetails(details);
  if (!obj) {
    return 0;
  }
  const metrics = readMetrics(details);
  const fromMetrics = metrics?.pagesFullyRedacted;
  if (typeof fromMetrics === 'number' && fromMetrics >= 0) {
    return fromMetrics;
  }
  const topLevel = obj.pagesFullyRedacted;
  return typeof topLevel === 'number' && topLevel >= 0 ? topLevel : 0;
}

// ---------------------------------------------------------------------------
// Multi-select hit-testing (VE-20838 marquee / lasso)
// ---------------------------------------------------------------------------

/**
 * Axis-aligned bounding box overlap test. Returns true when the two boxes
 * intersect (touching edges count). Used by the marquee tool to find
 * redactions that overlap the drag rectangle.
 */
export function aabbOverlaps(a: BoundingBox, b: BoundingBox): boolean {
  return (
    a.x <= b.x + b.width &&
    a.x + a.width >= b.x &&
    a.y <= b.y + b.height &&
    a.y + a.height >= b.y
  );
}

/**
 * Point-in-polygon test via the standard even-odd ray-casting algorithm.
 * The polygon is closed implicitly (last → first edge). Used by the lasso
 * tool to decide whether a redaction's representative point sits inside
 * the drawn path.
 */
export function polygonContainsPoint(
  poly: Array<{ x: number; y: number }>,
  point: { x: number; y: number }
): boolean {
  if (poly.length < 3) {
    return false;
  }
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    const intersects =
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Two redaction rects are considered the "same column of rows" — i.e.
 * stacked vertically with a gap small enough to be the inter-line space —
 * when they horizontally overlap AND the vertical gap between them is at
 * most `vGapAllowance`. `vGapAllowance` is computed off the taller of the
 * two rows so the threshold scales with the underlying line height and a
 * heading + body pair doesn't accidentally chain through a paragraph
 * break.
 */
function isAdjacentRow(
  a: BoundingBox,
  b: BoundingBox,
  vGapAllowance: number
): boolean {
  const hOverlap = a.x < b.x + b.width && a.x + a.width > b.x;
  if (!hOverlap) {
    return false;
  }
  const aBot = a.y + a.height;
  const bBot = b.y + b.height;
  // Negative gap = the rects overlap vertically (e.g. two redactions on
  // the exact same line). Either polarity counts as "adjacent enough".
  const gapBelowA = b.y - aBot;
  const gapAboveA = a.y - bBot;
  return gapBelowA <= vGapAllowance && gapAboveA <= vGapAllowance;
}

/**
 * BFS-cluster the redactions that form a contiguous vertical run with
 * `seed`. Starts at the seed and walks both up and down by chaining
 * neighbors that horizontally overlap and sit within ~one taller-row
 * height of each other vertically. Returns the cluster (including the
 * seed) as the list of matched ids.
 *
 * Page-scoped: only redactions on `pageNumber` (plus any `appliesToAllPages`
 * ones, which conceptually live on every page) participate. The
 * horizontal-overlap + vertical-adjacency heuristic doesn't try to detect
 * paragraph breaks explicitly — large gaps simply fail the adjacency test.
 */
export function clusterContiguousRows(
  all: Redaction[],
  seed: Redaction,
  pageNumber: number
): string[] {
  const candidates = all.filter(
    (r) => r.page === pageNumber || r.appliesToAllPages
  );
  const byId = new Map(candidates.map((r) => [r.id, r]));
  const cluster = new Set<string>([seed.id]);
  const frontier: string[] = [seed.id];
  while (frontier.length > 0) {
    const currentId = frontier.shift()!;
    const current = byId.get(currentId);
    if (!current) {
      continue;
    }
    for (const r of candidates) {
      if (cluster.has(r.id)) {
        continue;
      }
      // Threshold is generous on purpose — formal documents commonly use
      // 1.5x or larger line spacing, so the gap between two glyph-tight
      // redaction rects can be most of a glyph height. We allow up to a
      // full row height of vertical gap before declaring two rects
      // non-adjacent. Floor at 6 PDF units so very thin geometries (e.g.
      // an underline-only redaction) still chain to their neighbors.
      const vGap = Math.max(
        Math.max(current.geometry.height, r.geometry.height),
        6
      );
      if (isAdjacentRow(current.geometry, r.geometry, vGap)) {
        cluster.add(r.id);
        frontier.push(r.id);
      }
    }
  }
  return Array.from(cluster);
}

// ---------------------------------------------------------------------------
// PII engine result parsing (VE-21915)
//
// The document PII detection engine emits a `vtn-standard` asset whose `object`
// array holds one entry per detected PII occurrence, with normalized (0–1,
// top-left origin) bounding polys and an NER-style `label` (PERSON, LOCATION,
// ICD10_CODE, …). These helpers map that vocabulary onto the editor's fixed
// PiiTypeId taxonomy and convert the geometry into PDF user-space points,
// grouping repeated values into one detection with multiple occurrences (the
// same shape the rest of the editor consumes).
// ---------------------------------------------------------------------------

const DIRECT_PII_TYPE_SET = new Set<PiiTypeId>(DIRECT_PII_TYPES);

/**
 * Best-effort map from an engine NER label to the editor's PiiTypeId. Keyed by
 * the label upper-cased with non-alphanumerics collapsed to `_`, so minor
 * vocabulary drift (PHONE_NUMBER vs PHONE, EMAIL_ADDRESS vs EMAIL) still lands.
 * Unrecognized labels fall through to `'other'` so nothing is dropped.
 */
const ENGINE_LABEL_TO_PII_TYPE: Record<string, PiiTypeId> = {
  // Direct
  PERSON: 'full_name',
  PERSON_NAME: 'full_name',
  NAME: 'full_name',
  FULL_NAME: 'full_name',
  SSN: 'ssn',
  US_SSN: 'ssn',
  SOCIAL_SECURITY_NUMBER: 'ssn',
  DOB: 'dob',
  DATE_OF_BIRTH: 'dob',
  BIRTH_DATE: 'dob',
  PHONE: 'phone',
  PHONE_NUMBER: 'phone',
  TELEPHONE: 'phone',
  TELEPHONE_NUMBER: 'phone',
  EMAIL: 'email',
  EMAIL_ADDRESS: 'email',
  ADDRESS: 'address',
  LOCATION: 'address',
  GPE: 'address',
  US_ADDRESS: 'address',
  STREET_ADDRESS: 'address',
  LICENSE_PLATE: 'license_plate',
  VEHICLE_PLATE: 'license_plate',
  PLATE: 'license_plate',
  CREDIT_CARD: 'credit_card',
  CREDIT_CARD_NUMBER: 'credit_card',
  CREDIT_DEBIT_NUMBER: 'credit_card',
  MEDICAL_RECORD_NUMBER: 'medical_record_number',
  MRN: 'medical_record_number',
  ICD10_CODE: 'medical_record_number',
  ICD9_CODE: 'medical_record_number',
  MEDICAL_CODE: 'medical_record_number',
  HEALTH_CODE: 'medical_record_number',
  IP_ADDRESS: 'ip_address',
  IP: 'ip_address',
  IPV4: 'ip_address',
  IPV6: 'ip_address',
  // Indirect
  EMPLOYER: 'employer',
  ORG: 'employer',
  ORGANIZATION: 'employer',
  COMPANY: 'employer',
  SCHOOL: 'school',
  EDUCATION: 'school',
  UNIVERSITY: 'school',
  RACE: 'race',
  ETHNICITY: 'race',
  GENDER: 'gender',
  SEX: 'gender',
  AGE: 'age',
  RELIGION: 'religion',
  SALARY: 'salary',
  COMPENSATION: 'salary',
  INCOME: 'salary',
  VEHICLE: 'vehicle',
  VIN: 'vehicle',
  CAR: 'vehicle',
  IP_PREFIX: 'ip_prefix',
};

/**
 * Map a raw engine label onto a PiiTypeId. Falls back to `'other'` for any
 * label not in the table.
 */
export function mapEngineLabelToPiiType(label: string): PiiTypeId {
  const key = (label || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_');
  return ENGINE_LABEL_TO_PII_TYPE[key] ?? 'other';
}

/**
 * Convert a normalized (0–1, top-left origin) bounding poly into a PDF
 * user-space `BoundingBox` for the given page size. Returns null if the poly
 * is empty. Coordinates are rounded to the editor's coordinate precision.
 */
function boundingPolyToBox(
  poly: Array<{ x: number; y: number }>,
  pageWidth: number,
  pageHeight: number
): BoundingBox | null {
  if (!Array.isArray(poly) || poly.length === 0) {
    return null;
  }
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const round = (n: number) =>
    Number(n.toFixed(DOCUMENT_REDACTION_COORD_PRECISION));
  return {
    x: round(minX * pageWidth),
    y: round(minY * pageHeight),
    width: round((maxX - minX) * pageWidth),
    height: round((maxY - minY) * pageHeight),
  };
}

/**
 * Parse a document PII detection engine result into the editor's
 * `PiiDetection[]`.
 *
 * - Engine `label` → PiiTypeId via {@link mapEngineLabelToPiiType}.
 * - Engine `text` → the detection's display label.
 * - Normalized boundingPoly → PDF-point rect using `pageDimensions` (page is
 *   assumed 1-based, matching the editor).
 * - Occurrences sharing a (piiType, text) are grouped into one detection;
 *   occurrences on the same page accumulate rects.
 *
 * Detections are returned with `checked: false` / `expanded: false`; the
 * reducer applies any persisted toggle state.
 */
export function parsePiiEngineResults(
  result: PiiEngineResult,
  pageDimensions: PageDimensions
): PiiDetection[] {
  const objects = Array.isArray(result?.object) ? result.object : [];

  // Group key → accumulating detection. Insertion order is preserved so the
  // panel lists detections in the order the engine emitted them.
  const groups = new Map<
    string,
    {
      piiType: PiiTypeId;
      label: string;
      confidence: number;
      // Vendor group id (shared by every match of this phrase), when present.
      groupId?: string;
      // page → rects
      byPage: Map<number, BoundingBox[]>;
      // page → vendor match ids, positionally aligned 1:1 with `byPage` rects
      // (null where the engine omitted an id for that match).
      byPageIds: Map<number, (string | null)[]>;
    }
  >();

  for (const obj of objects) {
    if (!obj || typeof obj.page !== 'number') {
      continue;
    }
    const piiType = mapEngineLabelToPiiType(obj.label);
    const text = (obj.text ?? '').trim();
    // Fall back to the human label when the engine omits matched text so the
    // card still has something to show.
    const displayLabel = text || PII_TYPE_LABELS[piiType];
    const dims = pageDimensions[obj.page];
    if (!dims) {
      // No page size → can't place the rect. Skip this occurrence rather than
      // mis-position it; other occurrences of the same entity still load.
      continue;
    }
    const box = boundingPolyToBox(obj.boundingPoly, dims.width, dims.height);
    if (!box) {
      continue;
    }

    // Vendor ids live under `obj.vendor`. Prefer the vendor groupId so every
    // match of a phrase clusters into one card with a stable identity across
    // re-runs; fall back to the legacy piiType|text key for pre-id assets.
    const vendorId = obj.vendor?.id ?? null;
    const vendorGroupId = obj.vendor?.groupId;
    const groupKey = vendorGroupId
      ? `gid:${vendorGroupId}`
      : `${piiType}|${text.toLowerCase()}`;
    let group = groups.get(groupKey);
    if (!group) {
      group = {
        piiType,
        label: displayLabel,
        confidence: 0,
        groupId: vendorGroupId,
        byPage: new Map(),
        byPageIds: new Map(),
      };
      groups.set(groupKey, group);
    }
    group.confidence = Math.max(
      group.confidence,
      typeof obj.confidence === 'number' ? obj.confidence : 0
    );
    const pageRects = group.byPage.get(obj.page) ?? [];
    pageRects.push(box);
    group.byPage.set(obj.page, pageRects);
    // Keep ids positionally aligned with rects (push for every match, null
    // when the engine omitted an id) so rect[i] ↔ matchId[i] downstream.
    const pageIds = group.byPageIds.get(obj.page) ?? [];
    pageIds.push(vendorId);
    group.byPageIds.set(obj.page, pageIds);
  }

  const detections: PiiDetection[] = [];
  let idx = 0;
  for (const [groupKey, group] of groups) {
    // Stable detection id: the vendor group id when present, else a
    // deterministic key-derived id (stable across reloads of the same asset).
    const detId = group.groupId ?? `pii-${idx}-${slugifyForId(groupKey)}`;
    // Every detection gets a groupId + per-match ids so the per-match PII
    // format is always written. When the engine omits ids we synthesize
    // deterministic ones (`<detId>#<page>#<index>`) — positionally stable
    // across reloads of the same asset, and transparently replaced by real
    // vendor ids once the engine emits them.
    const groupId = group.groupId ?? detId;
    const occurrences: PiiOccurrence[] = [...group.byPage.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([page, rects]) => {
        const rawIds = group.byPageIds.get(page) ?? [];
        // 1:1 with rects (rect[i] ↔ matchId[i]); synthesize where missing.
        const matchIds = rects.map(
          (_, i) => rawIds[i] ?? `${detId}#${page}#${i}`
        );
        return { page, rects, id: matchIds[0], matchIds };
      });
    detections.push({
      // Discriminates the PII variant from lexicon-sourced detections, which
      // share the panel and the triage machinery (VE-24948 / U5).
      source: 'pii',
      id: detId,
      groupId,
      label: group.label,
      piiType: group.piiType,
      category: DIRECT_PII_TYPE_SET.has(group.piiType) ? 'direct' : 'indirect',
      confidence: group.confidence,
      occurrences,
      checked: false,
      expanded: false,
    });
    idx += 1;
  }
  return detections;
}

/**
 * Map each PII match id to its PDF-point bounding box (VE-22300), for the
 * redaction report's PII rows.
 *
 * Derived from `parsePiiEngineResults` rather than the raw engine objects so it
 * keys on the SAME match ids the persisted `PersistedPiiMatch` records use:
 * `parsePiiEngineResults` assigns `occ.matchIds[i]` from the vendor id when
 * present and a deterministic synthesized id (`<detId>#<page>#<index>`) when
 * not — and `buildPiiMatches` persists those exact ids. Keying off only
 * `vendor.id` would miss every match from an engine that omits vendor ids.
 * `occ.matchIds[i]` is positionally aligned 1:1 with `occ.rects[i]`.
 */
export function buildPiiMatchBoxes(
  result: PiiEngineResult,
  pageDimensions: PageDimensions
): Record<string, BoundingBox> {
  const boxes: Record<string, BoundingBox> = {};
  for (const d of parsePiiEngineResults(result, pageDimensions)) {
    for (const occ of d.occurrences ?? []) {
      const ids = occ.matchIds ?? (occ.id ? [occ.id] : []);
      ids.forEach((id, i) => {
        const box = occ.rects[i];
        if (box) {
          boxes[id] = box;
        }
      });
    }
  }
  return boxes;
}

/** Collapse a string to an id-safe slug (lowercase alphanumerics + dashes). */
function slugifyForId(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

// ---------------------------------------------------------------------------
// PII redaction state — page-level helper shared by the per-match
// persistence (VE-21915). Detection redaction state lives in
// `redactedMatchIds` (per vendor match id); this derives the page-level view
// some callers still use.
// ---------------------------------------------------------------------------

/** Pages of a detection that are currently redacted (checked). */
function redactedPagesOf(d: DetectionBase): number[] {
  const occurrences = d.occurrences ?? [];
  if (occurrences.length <= 1) {
    return d.checked === true ? occurrences.map((o) => o.page) : [];
  }
  const checked = new Set(d.checkedPages ?? []);
  return occurrences.map((o) => o.page).filter((p) => checked.has(p));
}

// ---------------------------------------------------------------------------
// Per-match PII selections (VE-21915) — added/redacted/code persistence,
// stored as `PersistedPiiMatch` records under each page bucket's `PII` array
// in the asset, keyed by vendor match id.
// ---------------------------------------------------------------------------

/**
 * Project the session's detections into a flat list of `PersistedPiiMatch`
 * records. One record per match id of a detection that's in the Redactions
 * list (`addedToRedactions`). `redacted` is page-scoped (the match is redacted
 * iff its page is in the detection's redacted-pages set). Matches that were
 * never added produce no record (a delete is represented by the record's
 * absence). `redactionsToAssetBody` buckets these under each page's `PII`.
 */
export function buildPiiMatches(
  detections: readonly Detection[]
): PersistedPiiMatch[] {
  const out: PersistedPiiMatch[] = [];
  for (const d of detections) {
    // Lexicon-sourced rows persist through `buildLexiconAssetRecords` instead.
    // They have no vendor `groupId`, so emitting them here would write records
    // keyed by a detection id into a bucket the PII loader reads back as vendor
    // matches (VE-24948 / U5).
    if (isLexiconDetection(d)) {
      continue;
    }
    if (!isPiiDetection(d)) {
      // Exhaustiveness at the TYPE level, tolerance at runtime.
      //
      // `session.detections` carries more than one source now, and the two
      // guards on it have opposite polarity: `mergePiiResultsIntoDetections`
      // KEEPS what it does not recognise, this one drops it — and on this path
      // a wrong guess silently loses the reviewer's redaction selections. The
      // `never` assignment makes adding a third source a compile error right
      // here, so the decision cannot be skipped (review, cross-cutting).
      //
      // It does NOT throw: a legacy detection with no `source` must not break a
      // save, and skipping is the behaviour that was already correct for it.
      const unhandled: never = d;
      void unhandled;
      continue;
    }
    if (d.addedToRedactions !== true) {
      continue;
    }
    const groupId = d.groupId ?? d.id;
    const perMatch = hasMatchIds(d);
    const redactedSet = new Set(d.redactedMatchIds ?? []);
    const deletedSet = new Set(d.deletedMatchIds ?? []);
    const redactedPages = new Set(redactedPagesOf(d));
    for (const occ of d.occurrences ?? []) {
      const ids = occ.matchIds ?? (occ.id ? [occ.id] : []);
      for (const id of ids) {
        // A deleted match keeps a record flagged `deleted` so the deletion
        // survives reload (a partially-deleted group remembers which matches
        // are gone); it's never redacted.
        const deleted = deletedSet.has(id);
        // Per-match redaction when the detection carries vendor ids; else the
        // legacy page-level flag applies to every match on the page.
        const redacted = deleted
          ? false
          : perMatch
            ? redactedSet.has(id)
            : redactedPages.has(occ.page);
        out.push({
          id,
          groupId,
          redacted,
          ...(deleted ? { deleted: true } : {}),
          piiType: d.piiType,
          text: d.label,
          page: occ.page,
          ...(d.redactionCode ? { redactionCode: d.redactionCode } : {}),
        });
      }
    }
  }
  return out;
}

/**
 * Re-apply persisted `PersistedPiiMatch` records to freshly-parsed detections
 * — the new primary restore path. Matches purely on the vendor match id (no
 * content/geometry overlap), so it survives re-positioned boxes and engine
 * re-runs as long as the ids are stable.
 *
 * A detection's card state is derived from its group's records: it's added
 * when any non-deleted record is present, and `checked` is all / partial /
 * none-redacted across the matched pages (so the card checkbox reflects
 * whether every match in the group is redacted — individual matches can be
 * redacted/unredacted via the overlay menu).
 */
/**
 * Fold freshly loaded PII engine results into the session's detection list
 * WITHOUT destroying rows the engine did not produce.
 *
 * `PII_RESULTS_LOADED` used to assign the engine's array straight onto
 * `session.detections`. That was correct while the list held nothing else, and
 * became a DATA-LOSS bug the moment custom lexicons put rows there
 * (VE-24948): applying a lexicon and then letting the engine finish — or
 * simply reopening a document, where results arrive after the saved lexicon
 * rows are restored — silently dropped every lexicon detection. The panel kept
 * reporting "N matches found" from `lexiconApply` and kept listing the lexicon
 * under "Applied to this document" from `appliedLexicons`, because those live
 * elsewhere in state; only the findings themselves were gone. Autosave then
 * wrote an asset with `appliedLexicons` and no `lexiconDetections`, making the
 * loss permanent.
 *
 * Engine rows come first so their existing order (and the panel's snapshots)
 * are untouched; the panel decides display order separately.
 */
export function mergePiiResultsIntoDetections(
  existing: readonly Detection[],
  enginePii: PiiDetection[],
  persistedMatches: PersistedPiiMatch[]
): Detection[] {
  return [
    ...applyPiiMatches(enginePii, persistedMatches),
    // Everything the PII engine cannot produce — today that is lexicon rows.
    ...existing.filter((d) => !isPiiDetection(d)),
  ];
}

export function applyPiiMatches(
  detections: PiiDetection[],
  matches: PersistedPiiMatch[]
): PiiDetection[] {
  if (!matches || matches.length === 0) {
    return detections;
  }
  // Index records by match id for direct occurrence lookup.
  const byId = new Map<string, PersistedPiiMatch>();
  for (const m of matches) {
    byId.set(m.id, m);
  }
  return detections.map((d) => {
    const redactedMatchIds: string[] = [];
    const deletedMatchIds: string[] = [];
    let added = false;
    let code: RedactionCodeAssignment | undefined;
    for (const occ of d.occurrences ?? []) {
      const ids = occ.matchIds ?? (occ.id ? [occ.id] : []);
      for (const id of ids) {
        const m = byId.get(id);
        if (!m) {
          continue;
        }
        if (m.deleted) {
          // Remember the deletion (hidden + excluded), but it doesn't count
          // as added.
          deletedMatchIds.push(id);
          continue;
        }
        // Presence (non-deleted) means the match was added to the list.
        added = true;
        if (m.redacted) {
          redactedMatchIds.push(id);
        }
        if (!code && m.redactionCode) {
          code = m.redactionCode;
        }
      }
    }
    if (
      !added &&
      redactedMatchIds.length === 0 &&
      deletedMatchIds.length === 0
    ) {
      return d;
    }
    // Per-match redaction set is the source of truth; checked / checkedPages
    // are derived for the card checkbox + legacy/UI consumers.
    const derived = deriveDetectionRedaction(
      { ...d, deletedMatchIds },
      redactedMatchIds
    );
    return {
      ...d,
      redactedMatchIds: derived.redactedMatchIds,
      checkedPages: derived.checkedPages,
      checked: derived.checked,
      ...(deletedMatchIds.length ? { deletedMatchIds } : {}),
      // Added when any non-deleted match is present.
      addedToRedactions: added,
      ...(code ? { redactionCode: code } : {}),
    };
  });
}
